import { useState, useEffect } from "react";
import { C } from "./re-colors.ts";
import { TEST_DATA, BOOK_OF_BUSINESS, BANK, PERSONAS, CECL_SCENARIOS, LENDING_PIPELINE, CUSTOMER_RISK_PROFILES, INVESTMENT_PORTFOLIO, CD_MATURITY_LADDER, LIQUIDITY_METRICS, MORTGAGE_PIPELINE, CONSUMER_LOANS, WEALTH_DATA, BRANCH_DATA, WATCHLIST_CREDITS, LOAN_MODIFICATIONS, AG_CROP_CONCENTRATION, DEPOSIT_PROFITABILITY, HMDA_LAR, FAIR_LENDING_DATA, DERIVATIVES_BOOK, MODEL_GOVERNANCE } from "./re-data.ts";
import { fmt, Bdg, Card, AiBox, KVList, Bar, SourcePill } from "./re-components.tsx";

// ── BASE DETAIL MODAL ─────────────────────────────────────────────
export function DetailModal({
  title, children, onClose, accentColor = C.burgundy,
}: {
  title: string; children: React.ReactNode; onClose: () => void; accentColor?: string;
}) {
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 600,
        background: "rgba(42,31,24,.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: C.bg, width: "min(92vw,860px)", maxHeight: "88vh",
        borderRadius: 16, display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(42,31,24,.4)", border: `1px solid ${C.border}`, overflow: "hidden",
      }}>
        <div style={{ background: accentColor, padding: "14px 20px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1, fontWeight: 800, fontSize: 15, color: "#fff" }}>{title}</div>
          <button
            onClick={onClose}
            style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: "6px 12px", fontWeight: 700, fontSize: 12 }}
          >
            ✕ Close
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

// ── STAT ROW HELPER ───────────────────────────────────────────────
function StatRow({ stats }: { stats: [string, string, string][] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length},1fr)`, gap: 12 }}>
      {stats.map(([l, v, c], i) => (
        <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, textAlign: "center" }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 6 }}>{l}</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: c }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

// ── DEPOSIT CONCENTRATION DETAIL ──────────────────────────────────
export function DepositConcentrationDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="Deposit Concentration Detail" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Deposits", "$3.62B", C.text],
          ["Uninsured Deposits", "$1.23B (34.1%)", C.burgundy],
          ["IntraFi Capacity", "$300M available", C.forest],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Top Deposit Concentrations</div>
          {TEST_DATA.deposits.topConcentrations.map((d, i) => (
            <div key={i} style={{ padding: "13px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{d.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{d.type}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(d.balance)}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{(d.pct * 100).toFixed(1)}% of total</div>
              <div style={{ fontSize: 12, color: d.uninsured > 0 ? C.burgundy : C.forest }}>
                {d.uninsured > 0 ? `${fmt(d.uninsured)} uninsured` : "Fully insured"}
              </div>
              <Bdg label={d.risk} color={d.risk === "critical" ? C.burgundy : d.risk === "high" ? C.gold : C.rust} />
            </div>
          ))}
        </Card>
        <AiBox
          text="Under correlated-exit scenario (Metro Motors + Regional Ag Coop exit simultaneously), 30-day stress outflow is $420M. Available liquidity stands at $624M — coverage ratio 148%, trending toward FDIC minimum of 125%. Recommend immediate IntraFi CDARS placement of $120M to reduce uninsured ratio to 30.8%."
          c={C.burgundy}
          source="Bancware Liquidity Stress Model · FDIC FIL-13-2023"
        />
      </div>
    </DetailModal>
  );
}

// ── SBA PIPELINE DETAIL ───────────────────────────────────────────
export function SBAPipelineDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="SBA Pipeline Detail" onClose={onClose} accentColor={C.forest}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Pipeline Value", "$42.0M", C.text],
          ["Est. Fee Income", "$420K", C.forest],
          ["Pre-Qualified", "7 clients", C.gold],
          ["vs Peer Median", "-34%", C.burgundy],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>SBA Pre-Qualified Leads</div>
          {TEST_DATA.sba.leads.map((l, i) => (
            <div key={i} style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 80px 1fr 1fr 80px", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{l.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{l.banker}</div>
              </div>
              <Bdg label={l.type} color={C.forest} />
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(l.amt)}</div>
              <div style={{ fontSize: 12, color: C.forest, fontWeight: 600 }}>Fee: {fmt(l.fee)}</div>
              <Bdg label={l.status} color={C.gold} />
            </div>
          ))}
        </Card>
        <AiBox
          text="7 existing clients are pre-qualified with on-file financials. J. Reed's 73% SBA conversion rate is highest on the team. A dedicated SBA specialist would yield estimated 4.4x ROI in year one. First-week outreach after new business license filing converts at 3.2x vs. 30-day outreach."
          c={C.forest}
          source="SBA ETRAN Lender Volume · Internal LOS · CA SBA District Office Peer Reports"
        />
      </div>
    </DetailModal>
  );
}

// ── NIM DETAIL ────────────────────────────────────────────────────
export function NIMDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="NIM & Rate Sensitivity" onClose={onClose} accentColor={C.gold}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Current NIM", "3.42%", C.text],
          ["QoQ Change", "-8bps", C.burgundy],
          ["Loan Yield", "5.84%", C.forest],
          ["Cost of Deposits", "1.94%", C.gold],
        ]} />
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 14 }}>Rate Sensitivity — NII Impact</div>
          {TEST_DATA.alm.rateSensitivity.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: i < TEST_DATA.alm.rateSensitivity.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ width: 80, fontSize: 12, fontWeight: 600, color: C.text }}>{s.scenario}</div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 6, background: C.surface, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${Math.abs(s.pct) * 400}%`,
                    background: s.pct >= 0 ? C.forest : C.burgundy,
                    marginLeft: s.pct < 0 ? `${100 - Math.abs(s.pct) * 400}%` : "0", borderRadius: 3,
                  }} />
                </div>
              </div>
              <div style={{ width: 80, fontSize: 12, fontWeight: 700, color: s.niiImpact >= 0 ? C.forest : C.burgundy, textAlign: "right" }}>
                {s.niiImpact >= 0 ? "+" : ""}{fmt(s.niiImpact)}
              </div>
              <div style={{ width: 50, fontSize: 11, color: C.muted, textAlign: "right" }}>
                {s.pct >= 0 ? "+" : ""}{(s.pct * 100).toFixed(1)}%
              </div>
            </div>
          ))}
        </Card>
        <AiBox
          text="Under base rate scenario with 2 Fed cuts priced for 2026, NIM projected to compress from 3.42% to 3.28%–3.30% by year-end, reducing NII by ~$8.2M annually. Pre-positioning $100M FHLB 5-yr advance at 4.28% before May FOMC would protect ~$420K NII. Recommend ALCO action at March 14 meeting."
          c={C.gold}
          source="Bancware ALM · CME Fed Funds Futures · FHLB Advance Rate Sheet"
        />
      </div>
    </DetailModal>
  );
}

// ── CECL DETAIL ───────────────────────────────────────────────────
export function CECLDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="CECL Reserve Detail" onClose={onClose} accentColor={C.forest}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total ACL", "$40.7M", C.text],
          ["ACL / Loans", "1.42%", C.gold],
          ["Peer Median", "1.31%", C.muted],
          ["Q-Factors", "$6.5M", C.burgundy],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Reserve by Segment</div>
          {TEST_DATA.cecl.segments.map((s, i) => (
            <div key={i} style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 80px", gap: 12, alignItems: "center" }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: C.text }}>{s.name}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{fmt(s.loans)}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{fmt(s.acl)}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: s.rate > 0.02 ? C.burgundy : s.rate > 0.015 ? C.gold : C.forest }}>
                {(s.rate * 100).toFixed(2)}%
              </div>
            </div>
          ))}
        </Card>
        <AiBox
          text="Ag drought Q-factor of $1.2M may need to increase to $1.8M–$2.1M if River County D3 drought designation persists through June. USDA D3 designation implies crop yield reductions of 25–35% for almonds and pistachios. CECL methodology well-documented; ACL 11bps above peer median provides adequate buffer."
          c={C.forest}
          source="FASB ASC 326 · USDA Drought Monitor · Moody's CRE Stress Scenarios"
        />
      </div>
    </DetailModal>
  );
}

// ── CRE CONCENTRATION DETAIL ──────────────────────────────────────
export function CREDetail({ onClose }: { onClose: () => void }) {
  const portfolioMix: [string, string, number, string][] = [
    ["CRE Non-Owner Occupied", "$1.04B", 36, C.burgundy],
    ["C&I",                    "$612M",  21, C.rust    ],
    ["Agricultural",           "$438M",  15, C.gold    ],
    ["Owner-Occupied CRE",     "$349M",  12, C.forest  ],
    ["Residential",            "$262M",   9, C.cyan    ],
    ["Consumer",               "$155M",   5, C.dim     ],
  ];
  return (
    <DetailModal title="CRE Concentration Detail" onClose={onClose} accentColor={C.gold}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["CRE Concentration", "287%", C.gold],
          ["Regulatory Threshold", "300%", C.text],
          ["Headroom", "$81M", C.forest],
          ["Non-Owner CRE", "$1.04B", C.burgundy],
        ]} />
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 14 }}>Portfolio Mix</div>
          {portfolioMix.map(([l, v, p, c], i) => (
            <div key={i} style={{ padding: "8px 0", borderBottom: i < 5 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: C.muted }}>{l}</span>
                <div style={{ display: "flex", gap: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{v}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{p}%</span>
                </div>
              </div>
              <Bar val={p} color={c} />
            </div>
          ))}
        </Card>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Criticized CRE Credits</div>
          {TEST_DATA.criticizedAssets.credits.filter(c => c.type === "CRE").map((c, i) => (
            <div key={i} style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{c.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{c.risk}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(c.bal)}</div>
              <div style={{ fontSize: 11, color: C.muted }}>Due: {c.due}</div>
              <Bdg label={c.cls} color={c.cls === "Doubtful" ? C.burgundy : c.cls === "Substandard" ? C.rust : C.gold} />
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── NPL DETAIL ────────────────────────────────────────────────────
export function NPLDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="NPL & Credit Quality Detail" onClose={onClose} accentColor={C.forest}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["NPL Ratio", "0.61%", C.forest],
          ["NPL Total", "$17.5M", C.gold],
          ["Non-Accrual", "$9.3M", C.burgundy],
          ["Net Charge-Offs", "0.08%", C.forest],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>All Criticized Assets</div>
          {TEST_DATA.criticizedAssets.credits.map((c, i) => (
            <div key={i} style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 80px 1fr 1fr auto", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{c.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{c.risk}</div>
              </div>
              <Bdg label={c.type} color={C.muted} />
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(c.bal)}</div>
              <div style={{ fontSize: 11, color: C.muted }}>Due: {c.due}</div>
              <Bdg label={c.cls} color={c.cls === "Doubtful" ? C.burgundy : c.cls === "Substandard" ? C.rust : C.gold} />
            </div>
          ))}
        </Card>
        <AiBox
          text="NPL ratio of 0.61% is below peer median of 0.74% — a relative strength. However, the criticized asset trend ($47.3M, up from $38M six months ago) warrants proactive management. Valley Ridge Growers shows 84% probability of Substandard migration per AI credit model."
          c={C.forest}
          source="AFS Credit Platform · FDIC Peer Comparison"
        />
      </div>
    </DetailModal>
  );
}

// ── AT-RISK REVENUE DETAIL ────────────────────────────────────────
export function AtRiskRevenueDetail({ onClose }: { onClose: () => void }) {
  const rc: Record<string, string> = { high: C.burgundy, medium: C.gold, low: C.forest };
  return (
    <DetailModal title="At-Risk Revenue & Relationship Retention" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["At-Risk Revenue", "$218K", C.burgundy],
          ["Retention Rate YTD", "94%", C.forest],
          ["Avg Products/HH", "3.8", C.gold],
          ["Total Relationships", "7", C.text],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Relationship Retention Scorecard</div>
          {TEST_DATA.crm.relationships.map((r, i) => (
            <div key={i} style={{ padding: "13px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 60px auto", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{r.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{r.action}</div>
              </div>
              <div style={{ fontSize: 12, color: C.text }}>{fmt(r.loan)} loan</div>
              <div style={{ fontSize: 12, color: C.muted }}>{fmt(r.dep)} dep</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: r.score > 80 ? C.forest : r.score > 70 ? C.gold : C.burgundy }}>{r.score}</div>
              <Bdg label={r.risk} color={rc[r.risk]} />
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── PORTFOLIO LOANS DETAIL ────────────────────────────────────────
export function PortfolioLoansDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="Portfolio Loans Detail" onClose={onClose} accentColor={C.forest}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Loans", "$47.3M", C.text],
          ["MTD Growth", "+$2.1M", C.forest],
          ["Watch Credits", "3", C.gold],
          ["Avg DSCR", "1.55x", C.forest],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Loan Portfolio — All Credits</div>
          {BOOK_OF_BUSINESS.filter(c => c.loan > 0).map((c, i) => (
            <div key={i} style={{ padding: "13px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 80px 1fr 1fr 60px", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{c.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{c.type}</div>
              </div>
              <Bdg label={c.status} color={c.status === "Watch" ? C.gold : c.status === "Prospect" ? C.navy : C.forest} />
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(c.loan)}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{c.nextMaturity || "—"}</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: c.score > 80 ? C.forest : c.score > 70 ? C.gold : C.burgundy }}>{c.score}</div>
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── COMPETITOR ACTIVITY DETAIL ────────────────────────────────────
export function CompetitorDetail({ onClose }: { onClose: () => void }) {
  const steps = [
    { step: "1", action: "Call today — within 48 hours of inquiry",       detail: "Outreach within 48hrs has 3x higher retention rate than 5-day outreach",    urgency: "critical" },
    { step: "2", action: "Present rate hold for 90 days",                 detail: "Lock current rate to neutralize WF pricing offer; est. cost $2,400",         urgency: "high"     },
    { step: "3", action: "Highlight local decision authority",            detail: "Community bank advantage: decision in 3.2 days vs. WF 18 days",              urgency: "medium"   },
    { step: "4", action: "Cross-sell treasury management if retained",    detail: "Est. $14K additional annual fee — increases switching cost",                  urgency: "low"      },
  ];
  const urgencyColor: Record<string, string> = { critical: C.burgundy, high: C.gold, medium: C.rust, low: C.forest };
  return (
    <DetailModal title="Competitor Activity — Apex Medical Group" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ background: `${C.burgundy}08`, border: `1px solid ${C.burgundy}30` }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.burgundy, marginBottom: 8, textTransform: "uppercase", letterSpacing: .8 }}>
            🚨 Alert: Competitor Credit Inquiry Detected
          </div>
          <KVList rows={[
            ["Account",         "Apex Medical Group"],
            ["Inquiry Source",  "Wells Fargo"],
            ["Inquiry Date",    "Feb 24, 2026"],
            ["Current Loan Balance", "$3.1M"],
            ["Current Deposits",     "$2.2M"],
            ["Annual Revenue",       "$168,000"],
            ["Relationship Score",   "88 / 100"],
            ["Products",             "5 — C&I Term, LOC, Checking, MMDA, Merchant"],
          ]} />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Recommended Response Strategy</div>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: urgencyColor[s.urgency], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                {s.step}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{s.action}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s.detail}</div>
              </div>
            </div>
          ))}
        </Card>
        <AiBox
          text="Pre-emptive rate hold with a 90-day guarantee has a 3x higher retention rate compared to waiting. Sunrise Medical at score 88 is the highest-value relationship in the portfolio at $168K annual revenue. Losing this relationship would require 3+ new mid-market clients to replace."
          c={C.burgundy}
          source="Experian BIS Business Credit Monitor · Salesforce FSC Retention Model"
        />
      </div>
    </DetailModal>
  );
}

// ── NEW PROSPECTS DETAIL ──────────────────────────────────────────
export function ProspectsDetail({ onClose }: { onClose: () => void }) {
  const prospects = [
    { name: "Eastview Dental Associates", industry: "Healthcare",    licenses: "CA SOS Feb 28", loanNeed: "$480K SBA 7(a)",    fee: "$14.4K", priority: "high",   contact: "Dr. S. Morris" },
    { name: "Valley Tire & Lube",        industry: "Auto Service",  licenses: "CA SOS Mar 1",  loanNeed: "$220K SBA Microloan", fee: "$4.4K",  priority: "medium", contact: "Mike Barajas"  },
    { name: "Coastal Imports LLC",       industry: "Retail / Import",licenses: "CA SOS Mar 1",  loanNeed: "$1.1M SBA 7(a)",    fee: "$33K",   priority: "high",   contact: "Tony Nguyen"   },
  ];
  return (
    <DetailModal title="New Business Prospects — This Week" onClose={onClose} accentColor={C.forest}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ background: `${C.forest}08`, border: `1px solid ${C.forest}30` }}>
          <div style={{ fontSize: 12, color: C.muted }}>
            3 new CA Secretary of State business filings this week match SBA 7(a) or Microloan profile in Jordan Reed's territory. First-week outreach converts at <strong>3.2x</strong> vs. 30-day outreach.
          </div>
        </Card>
        {prospects.map((p, i) => (
          <Card key={i} style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: 4, background: p.priority === "high" ? C.forest : C.gold, borderRadius: "12px 0 0 12px", flexShrink: 0 }} />
              <div style={{ padding: "14px 16px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{p.industry} · Filed: {p.licenses}</div>
                  </div>
                  <Bdg label={p.priority === "high" ? "High Priority" : "Medium"} color={p.priority === "high" ? C.forest : C.gold} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  {[["Loan Need", p.loanNeed, C.text], ["Est. Fee", p.fee, C.forest], ["Contact", p.contact, C.text]].map(([label, val, clr], j) => (
                    <div key={j}>
                      <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .5 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: clr }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        ))}
        <AiBox
          text="Combined loan need: $1.8M. Combined estimated fee income: $51.8K. Outreach this week vs. waiting 30 days is statistically 3.2x more likely to convert. Eastview Dental and Coastal Imports are highest priority based on loan size and fee potential."
          c={C.forest}
          source="CA Secretary of State Filings · SBA 7(a) SIC Approval Rate Database"
        />
      </div>
    </DetailModal>
  );
}

// ── ACH DETAIL ────────────────────────────────────────────────────
export function ACHDetail({ onClose }: { onClose: () => void }) {
  const returnCodes = [
    { code: "R01", desc: "Insufficient Funds (NSF)",        count: 3, employers: "Regional Commercial Growers",  risk: "high"   },
    { code: "R02", desc: "Account Closed",                  count: 2, employers: "Westside Payroll Services",   risk: "high"   },
    { code: "R03", desc: "No Account / Unable to Locate",   count: 1, employers: "Various",                    risk: "medium" },
    { code: "R16", desc: "Account Frozen",                  count: 1, employers: "Central Valley Auto",        risk: "medium" },
  ];
  return (
    <DetailModal title="ACH Exceptions & Return Rate" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["ACH Exceptions", "7", C.burgundy],
          ["Return Rate", "2.4%", C.burgundy],
          ["Nacha Threshold", "1.5%", C.text],
          ["Wires Today", "43 / $12.4M", C.gold],
        ]} />
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Return Code Breakdown</div>
          {returnCodes.map((r, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < 3 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ width: 36, fontSize: 10, fontWeight: 800, color: C.burgundy, background: C.burgundyBg, padding: "3px 6px", borderRadius: 6 }}>{r.code}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{r.desc}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{r.employers}</div>
              </div>
              <span style={{ fontWeight: 800, fontSize: 16, color: C.burgundy }}>{r.count}</span>
              <Bdg label={r.risk} color={r.risk === "high" ? C.burgundy : C.gold} />
            </div>
          ))}
        </Card>
        <AiBox
          text="Regional Commercial Growers had 2 payroll returns in 30 days — a leading indicator of business distress. Recommend credit review of any lending relationships. Nacha formal warning triggers at 3.0% return rate; current trajectory will reach that threshold within 3–4 business days without intervention."
          c={C.burgundy}
          source="FedACH Return Rate Monitor · Nacha ACH Rules 2026 · FIS Horizon Exception Queue"
        />
      </div>
    </DetailModal>
  );
}

// ── OFAC DETAIL ───────────────────────────────────────────────────
export function OFACDetail({ onClose }: { onClose: () => void }) {
  const actions = [
    "Block the wire — do NOT release pending full BSA review",
    "Contact OFAC Hotline: 1-800-540-6322 to confirm SDN match",
    "Freeze related accounts per 31 CFR § 501.603",
    "Document all actions in Verafin case file within 1 hour",
    "File OFAC report within 10 business days of block",
    "Notify BSA Officer and General Counsel immediately",
  ];
  return (
    <DetailModal title="OFAC Wire Hold — Al Rashid Trading" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ background: `${C.burgundy}08`, border: `1px solid ${C.burgundy}40` }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.burgundy, marginBottom: 10, textTransform: "uppercase", letterSpacing: .8 }}>
            🚨 OFAC Hold — Immediate Action Required · 2-Hour Review Window
          </div>
          <KVList rows={[
            ["Account Holder",   "Al Rashid Trading LLC"],
            ["Wire Amount",      "$28,400"],
            ["Destination",      "UAE — First Abu Dhabi Bank"],
            ["SDN Match Score",  "94%"],
            ["Match Name",       "Al Rashid, Fawaz Yahya"],
            ["Hold Placed",      "8:42 AM, March 2, 2026"],
            ["Review Deadline",  "10:42 AM, March 2, 2026"],
            ["Assigned To",      "Kelly Ford — BSA/Compliance"],
          ]} />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Required Actions (in sequence)</div>
          {actions.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 10, padding: "9px 0", borderBottom: i < 5 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: i < 3 ? C.burgundy : C.gold, color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {i + 1}
              </div>
              <div style={{ fontSize: 12, color: C.text }}>{a}</div>
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── VAULT DISCREPANCY DETAIL ──────────────────────────────────────
export function VaultDetail({ onClose }: { onClose: () => void }) {
  const steps = [
    "Complete dual-control recount with branch manager present",
    "Review all negotiable instrument logs from Feb 28–Mar 2",
    "Check for any unprocessed cash-back transactions",
    "Document findings in branch exception log within 2 hours",
    "If unresolved after recount, escalate to Operations immediately",
    "Update FIS Horizon vault module with certified count",
  ];
  return (
    <DetailModal title="North Branch — Vault Discrepancy" onClose={onClose} accentColor={C.gold}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ background: `${C.gold}08`, border: `1px solid ${C.gold}40` }}>
          <KVList rows={[
            ["Branch",                "Northside — 7200 N. Northside Ave"],
            ["System Vault Balance",  "$248,600"],
            ["Physical Count",        "$252,800"],
            ["Discrepancy",           "+$4,200 (overage)"],
            ["Last Reconciled",       "Feb 28, 2026"],
            ["Last Counted By",       "T. Harris (single control)"],
            ["Dual Control Required", "Yes — not yet completed"],
          ]} />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Resolution Steps</div>
          {steps.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: i < 5 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: i < 2 ? C.burgundy : C.gold, color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {i + 1}
              </div>
              <div style={{ fontSize: 12, color: C.text }}>{a}</div>
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── REG CC TRAINING DETAIL ────────────────────────────────────────
export function RegCCDetail({ onClose }: { onClose: () => void }) {
  const branches = [
    { branch: "Main Branch",   pct: 88, done: 22, total: 25 },
    { branch: "North Branch", pct: 60, done:  9, total: 15 },
    { branch: "Regional Hub",   pct: 50, done:  8, total: 16 },
    { branch: "West Branch", pct: 71, done: 10, total: 14 },
    { branch: "Commerce Branch", pct: 92, done: 11, total: 12 },
    { branch: "East Branch",    pct: 55, done:  6, total: 11 },
  ];
  return (
    <DetailModal title="Reg CC Training Status" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Completed",        "87 of 143", C.forest],
          ["Completion Rate",  "61%",       C.burgundy],
          ["Deadline",         "Mar 9, 2026", C.burgundy],
          ["Days Remaining",   "7",         C.burgundy],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Completion by Branch</div>
          {branches.map((b, i) => (
            <div key={i} style={{ padding: "11px 16px", borderTop: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{b.branch}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: b.pct >= 80 ? C.forest : b.pct >= 60 ? C.gold : C.burgundy }}>
                  {b.pct}% ({b.done}/{b.total})
                </span>
              </div>
              <Bar val={b.pct} color={b.pct >= 80 ? C.forest : b.pct >= 60 ? C.gold : C.burgundy} />
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── SAR DETAIL ────────────────────────────────────────────────────
export function SARDetail({ onClose }: { onClose: () => void }) {
  const transactions: [string, string, string, string][] = [
    ["Feb 7",  "Main Branch",   "$9,800 cash", "9:14 AM" ],
    ["Feb 9",  "North Branch", "$9,600 cash", "2:31 PM" ],
    ["Feb 11", "Main Branch",   "$9,750 cash", "11:02 AM"],
    ["Feb 13", "Regional Hub",   "$8,750 cash", "3:45 PM" ],
    ["Feb 14", "North Branch", "$9,500 cash", "10:18 AM"],
    ["Feb 17", "Main Branch",   "$9,800 cash", "1:22 PM" ],
    ["Feb 19", "Eastview",       "$9,200 cash", "4:01 PM" ],
    ["Feb 21", "West Branch", "$9,800 cash", "2:55 PM" ],
  ];
  return (
    <DetailModal title="SAR Filing — Account #4471" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ background: `${C.burgundy}08`, border: `1px solid ${C.burgundy}40` }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.burgundy, marginBottom: 10, textTransform: "uppercase", letterSpacing: .8 }}>
            🚨 SAR Filing Deadline: TODAY — March 2, 2026
          </div>
          <KVList rows={[
            ["Account Holder",     "J. Smith DBA Metro Imports"],
            ["Account Number",     "#4471"],
            ["Suspicious Activity","Structuring — 8 cash deposits"],
            ["Amount Range",       "$8,750–$9,800 per deposit"],
            ["Period",             "Feb 7–21, 2026 (14 days)"],
            ["Branches Involved",  "4 branches (split structuring)"],
            ["Total Amount",       "$76,200 structured"],
            ["Detection Method",   "Verafin Pattern Engine — Rule 47"],
          ]} />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Transaction Pattern</div>
          {transactions.map(([d, br, amt, t], i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "80px 1fr 1fr 80px", padding: "8px 0", borderBottom: i < 7 ? `1px solid ${C.border}` : "none", gap: 12 }}>
              <span style={{ fontSize: 11, color: C.muted }}>{d}</span>
              <span style={{ fontSize: 11, color: C.text }}>{br}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.burgundy }}>{amt}</span>
              <span style={{ fontSize: 11, color: C.dim }}>{t}</span>
            </div>
          ))}
        </Card>
        <AiBox
          text="Pattern matches FinCEN Advisory FIN-2026-A001 structuring rings targeting Central Valley ag businesses. Filing must be submitted to FinCEN BSA E-Filing by end of business today to meet the 30-day SAR window from Feb 1 detection date. Draft is 90% complete in Verafin — 15 minutes to finalize."
          c={C.burgundy}
          source="FinCEN Advisory FIN-2026-A001 · Verafin · Bank Secrecy Act 31 U.S.C. § 5318(g)"
        />
      </div>
    </DetailModal>
  );
}

// ── CRA GAPS DETAIL ───────────────────────────────────────────────
export function CRADetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="CRA LMI Tract Gaps" onClose={onClose} accentColor={C.forest}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["CRA Rating",      "Outstanding", C.forest],
          ["LMI Lending",     "18.4%",       C.gold  ],
          ["Lending Test",    "92/100",      C.forest],
          ["LMI Tract Gaps",  "3 tracts",   C.burgundy],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>LMI Assessment Area Gaps</div>
          {TEST_DATA.cra.lmiGapTracts.map((t, i) => (
            <div key={i} style={{ padding: "13px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "80px 2fr 80px 80px 80px", gap: 12, alignItems: "center" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.navy }}>{t.tract}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{t.area}</div>
                <div style={{ fontSize: 11, color: C.muted }}>LMI: {t.lmiPct}%</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.burgundy }}>{t.loansYTD} loans</div>
              <div style={{ fontSize: 11, color: C.muted }}>{t.peers} peer loans</div>
              <Bdg label="Gap" color={C.burgundy} />
            </div>
          ))}
        </Card>
        <AiBox
          text="All 3 LMI gap tracts have 2–4 peer lenders active with zero bank loans YTD. A CDFI partnership in SW Metro (Tract 2011.01, 78% LMI) could contribute $1.2M LMI lending credit toward Q4 exam. Recommend outreach to local CDFI partners."
          c={C.forest}
          source="FFIEC CRA Reporting · HMDA LAR · CDFI Fund Member Directory"
        />
      </div>
    </DetailModal>
  );
}

// ── HMDA DETAIL ───────────────────────────────────────────────────
export function HMDADetail({ onClose }: { onClose: () => void }) {
  const exceptions = [
    { id: "LAR-2026-0112", issue: "GMI form missing — in-person application", branch: "Main Branch", banker: "J. Reed",    status: "Pending" },
    { id: "LAR-2026-0118", issue: "GMI form missing — in-person application", branch: "Northside",      banker: "M. Torres",     status: "Pending" },
    { id: "LAR-2026-0124", issue: "GMI form missing — in-person application", branch: "Lakeside",     banker: "K. Perry",    status: "Pending" },
    { id: "LAR-2026-0131", issue: "GMI form missing — in-person application", branch: "Eastview",     banker: "T. Harris",status: "Pending" },
    { id: "LAR-2026-0144", issue: "Mail-in app — ethnicity field incomplete",  branch: "Main",        banker: "L. Yamamoto", status: "Pending" },
    { id: "LAR-2026-0151", issue: "Mail-in app — race field incomplete",       branch: "Westside",     banker: "C. Chang",     status: "Pending" },
  ];
  return (
    <DetailModal title="HMDA LAR Exceptions" onClose={onClose} accentColor={C.gold}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Exceptions", "6",           C.burgundy],
          ["LAR Deadline",     "Mar 15, 2026", C.burgundy],
          ["Days Remaining",   "13",          C.gold    ],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Exception Detail</div>
          {exceptions.map((e, i) => (
            <div key={i} style={{ padding: "11px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "120px 2fr 1fr 100px", gap: 12, alignItems: "center" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.navy }}>{e.id}</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{e.issue}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{e.branch} — {e.banker}</div>
              </div>
              <div />
              <Bdg label={e.status} color={C.gold} />
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── BSA OVERDUE REVIEWS DETAIL ────────────────────────────────────
export function BSAReviewsDetail({ onClose }: { onClose: () => void }) {
  const categories = [
    { cat: "Money Service Businesses (MSBs)",  count: 3, daysPastDue: "45–90", priority: "critical" },
    { cat: "Cannabis-Adjacent Businesses",      count: 4, daysPastDue: "30–60", priority: "high"     },
    { cat: "International Wire Customers",      count: 7, daysPastDue: "15–45", priority: "high"     },
  ];
  return (
    <DetailModal title="Overdue High-Risk BSA Reviews" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Overdue Reviews",    "14", C.burgundy],
          ["MSBs",              "3",  C.burgundy],
          ["Cannabis-Adjacent", "4",  C.gold    ],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Overdue Reviews by Category</div>
          {categories.map((r, i) => (
            <div key={i} style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 60px 100px auto", gap: 12, alignItems: "center" }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{r.cat}</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.burgundy }}>{r.count}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{r.daysPastDue} days overdue</div>
              <Bdg label={r.priority} color={r.priority === "critical" ? C.burgundy : C.gold} />
            </div>
          ))}
        </Card>
        <AiBox
          text="FDIC examiners cited delayed high-risk customer annual reviews as an MRA in 68% of community bank exams in 2025. With the May 2026 exam window approaching, completing all 14 reviews by March 31 provides a 30-day buffer. Recommend assigning MSBs to senior BSA analyst immediately."
          c={C.burgundy}
          source="FDIC Supervisory Findings Database 2025 · FFIEC BSA/AML Examination Manual"
        />
      </div>
    </DetailModal>
  );
}

// ── OAKS STREET DETAIL ────────────────────────────────────────────
export function OaksStreetDetail({ onClose }: { onClose: () => void }) {
  const actions = [
    { action: "Call borrower today — initiate maturity discussion",        owner: "D. Nakamura / J. Reed", due: "Today",  priority: "critical" },
    { action: "Order updated MAI appraisal",                               owner: "Credit Admin",             due: "Mar 9",  priority: "high"     },
    { action: "Request 2025 operating statements + rent roll",             owner: "J. Reed",                due: "Mar 9",  priority: "high"     },
    { action: "Model paydown options — min 10% principal reduction",       owner: "D. Nakamura",              due: "Mar 14", priority: "high"     },
    { action: "Present to Credit Committee",                               owner: "D. Nakamura",              due: "Mar 19", priority: "medium"   },
    { action: "Issue maturity extension or modified commitment letter",    owner: "Lending Officer",          due: "Apr 1",  priority: "medium"   },
  ];
  return (
    <DetailModal title="Meridian Properties Group — Credit Action Required" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Card style={{ background: `${C.burgundy}08`, border: `1px solid ${C.burgundy}40` }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.burgundy, marginBottom: 10, textTransform: "uppercase", letterSpacing: .8 }}>
            🚨 Maturity April 16, 2026 — 45 Days Away
          </div>
          <KVList rows={[
            ["Borrower",        "Meridian Properties Group LLC"],
            ["Loan Type",       "CRE Term Loan"],
            ["Balance",         "$6.7M"],
            ["Classification",  "Special Mention"],
            ["Maturity",        "April 16, 2026"],
            ["DSCR (Current)",  "1.11x ↓ (orig. 1.38x)"],
            ["LTV",             "72%"],
            ["Collateral",      "Office complex — Meridian Street, Metro City"],
            ["Last Review",     "Nov 2025 (due)"],
          ]} />
        </Card>
        <Card>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>Action Plan</div>
          {actions.map((a, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px auto", gap: 12, padding: "9px 0", borderBottom: i < 5 ? `1px solid ${C.border}` : "none", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{a.action}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{a.owner}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{a.due}</div>
              <Bdg label={a.priority} color={a.priority === "critical" ? C.burgundy : a.priority === "high" ? C.gold : C.rust} />
            </div>
          ))}
        </Card>
      </div>
    </DetailModal>
  );
}

// ── AG DROUGHT DETAIL ─────────────────────────────────────────────
export function AgDroughtDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="Agricultural Drought Exposure" onClose={onClose} accentColor={C.gold}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Ag Portfolio",  "$438M",        C.text    ],
          ["Drought-Affected",    "$67M / 14 loans",C.burgundy],
          ["Insurance Verified",  "9 of 14",      C.gold    ],
          ["Q-Factor (CECL)",     "$1.2M",        C.gold    ],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Key Ag Credits</div>
          {TEST_DATA.agricultural.credits.map((c, i) => (
            <div key={i} style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 80px 1fr 2fr", gap: 12, alignItems: "center" }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{c.name}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{c.crop}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmt(c.bal)}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: c.ins === "Verified" ? C.forest : C.burgundy, fontWeight: 700 }}>
                  {c.ins === "Verified" ? "✓" : "⚠"} {c.ins}
                </span>
                <span style={{ fontSize: 11, color: C.muted }}>— {c.action}</span>
              </div>
            </div>
          ))}
        </Card>
        <AiBox
          text="River County upgraded to USDA D3 Extreme Drought Feb 20. D3 designation implies crop yield reductions of 25–35% for almonds and pistachios. The current $1.2M drought Q-factor may need to increase to $1.8M–$2.1M if D3 persists through June. 5 borrowers with unverified insurance represent $31M in unprotected exposure."
          c={C.gold}
          source="USDA Drought Monitor Feb 20, 2026 · USDA NASS Historical Yield Data · FASB ASC 326"
        />
      </div>
    </DetailModal>
  );
}

// ── DOCUMENT GENERATION MODAL ─────────────────────────────────────
export function DocModal({
  title, onClose, personaName, prompt, bankCtx,
}: {
  title: string; onClose: () => void; personaName: string; prompt: string; bankCtx: string;
}) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://api.anthropic.com/v1/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            model: "claude-sonnet-4-20250514",
            max_tokens: 1500,
            system: `You are a banking document assistant for Demo Regional Bank. Generate professional, complete banking documents. Be specific with numbers. Format clearly with sections. Do not use markdown headers with #. Use ALL CAPS for section titles followed by a newline. Today is March 2, 2026.`,
            messages: [{ role: "user", content: `${prompt}\n\nBank context: ${bankCtx}` }],
          }),
        });
        const d = await res.json();
        setContent(d.content?.find((b: any) => b.type === "text")?.text || "Unable to generate document.");
      } catch {
        setContent("Connection error. Please try again.");
      }
      setLoading(false);
    })();
  }, []);

  function copy() {
    navigator.clipboard?.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 700, background: "rgba(42,31,24,.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: C.bg, width: "90vw", maxWidth: 780, maxHeight: "88vh", borderRadius: 16, display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(42,31,24,.4)", border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ background: C.burgundy, padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 18 }}>📄</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: "#fff" }}>{title}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)" }}>Generated for {personaName} · {BANK.asOfDate}</div>
          </div>
          <button onClick={copy} style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: "6px 12px", fontWeight: 700, fontSize: 11 }}>
            {copied ? "✓ Copied" : "Copy"}
          </button>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: "6px 12px", fontWeight: 700, fontSize: 11 }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 200, gap: 12 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {[0, 1, 2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: C.burgundy, animation: `dot .9s ${i * .2}s infinite` }} />)}
              </div>
              <span style={{ fontSize: 12, color: C.muted }}>Generating document…</span>
            </div>
          ) : (
            <pre style={{ fontSize: 12, color: C.text, lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "inherit", margin: 0 }}>{content}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ESCALATION MODAL ──────────────────────────────────────────────
export function EscalateModal({
  onClose, fromPersona, personas, onEscalate,
}: {
  onClose: () => void;
  fromPersona: typeof PERSONAS[0];
  personas: typeof PERSONAS;
  onEscalate: (data: { from: string; fromRole: string; to: string; msg: string; priority: string }) => void;
}) {
  const [toRole, setToRole] = useState("");
  const [msg, setMsg] = useState("");
  const [priority, setPriority] = useState("high");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const prioColor: Record<string, string> = { critical: C.burgundy, high: C.gold, medium: C.rust };
  const toPerson = personas.find(p => p.id === toRole);

  function submit() {
    if (!toRole || !msg) return;
    setSending(true);
    // Simulate 800ms delivery processing
    setTimeout(() => {
      onEscalate({ from: fromPersona.name, fromRole: fromPersona.full, to: toRole, msg, priority });
      setSending(false);
      setSent(true);
    }, 800);
  }

  const CHANNELS = [
    { icon: "🔔", label: "In-App Notification", detail: `Delivered to ${toPerson?.name || "recipient"}'s dashboard alert feed`, status: "Delivered", color: C.forest },
    { icon: "✉️", label: "Email", detail: `Queued to ${(toPerson?.name || "recipient").toLowerCase().replace(" ", ".")}@valleyfirstbank.com`, status: "Queued", color: C.gold },
    { icon: "💬", label: "Microsoft Teams", detail: `@mention sent in #escalations-${priority} channel`, status: "Sent", color: C.forest },
    { icon: "📱", label: "SMS", detail: priority === "critical" ? "Critical priority — SMS dispatched to registered mobile" : "High/Medium — SMS suppressed per notification policy", status: priority === "critical" ? "Sent" : "Suppressed", color: priority === "critical" ? C.forest : C.muted },
  ];

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 600, background: "rgba(42,31,24,.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: C.bg, width: 500, borderRadius: 16, boxShadow: "0 24px 80px rgba(42,31,24,.4)", border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ background: sent ? C.forest : C.burgundy, padding: "14px 20px", display: "flex", alignItems: "center", gap: 10, transition: "background .3s" }}>
          <span style={{ fontSize: 16 }}>{sent ? "✅" : "📨"}</span>
          <div style={{ flex: 1, fontWeight: 800, fontSize: 14, color: "#fff" }}>{sent ? "Escalation Delivered" : "Escalate Alert"}</div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.25)", borderRadius: 8, color: "#fff", cursor: "pointer", padding: "5px 11px", fontWeight: 700, fontSize: 12 }}>✕</button>
        </div>

        {sent ? (
          /* ── DELIVERY CONFIRMATION SCREEN ── */
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Summary */}
            <div style={{ background: `${C.forest}0A`, border: `1px solid ${C.forest}30`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                {fromPersona.icon} {fromPersona.name} → {toPerson?.icon} {toPerson?.name}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: `${prioColor[priority]}18`, color: prioColor[priority], border: `1px solid ${prioColor[priority]}30`, textTransform: "capitalize" }}>{priority}</span>
                <span style={{ fontSize: 10, color: C.dim }}>· Logged to audit trail · {new Date().toLocaleTimeString()}</span>
              </div>
              <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, fontStyle: "italic" }}>"{msg}"</div>
            </div>

            {/* Delivery channels */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Delivery Channels</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {CHANNELS.map((ch, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: C.surface, borderRadius: 9, border: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: 18, width: 28, textAlign: "center", flexShrink: 0 }}>{ch.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{ch.label}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{ch.detail}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: `${ch.color}15`, color: ch.color, border: `1px solid ${ch.color}30`, flexShrink: 0 }}>{ch.status}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={onClose}
              style={{ background: C.forest, color: "#fff", border: "none", borderRadius: 10, padding: "11px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
            >
              Done
            </button>
          </div>
        ) : (
          /* ── COMPOSE SCREEN ── */
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Send To</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {personas.filter(p => p.id !== fromPersona.id).map(p => (
                  <button key={p.id} onClick={() => setToRole(p.id)} style={{ padding: "7px 13px", borderRadius: 20, border: `1px solid ${toRole === p.id ? p.color : C.border}`, background: toRole === p.id ? `${p.color}15` : "transparent", cursor: "pointer", fontSize: 12, fontWeight: toRole === p.id ? 700 : 400, color: toRole === p.id ? p.color : C.muted }}>
                    {p.icon} {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Priority</div>
              <div style={{ display: "flex", gap: 8 }}>
                {(["critical", "high", "medium"] as const).map(p => (
                  <button key={p} onClick={() => setPriority(p)} style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid ${priority === p ? prioColor[p] : C.border}`, background: priority === p ? `${prioColor[p]}15` : "transparent", cursor: "pointer", fontSize: 11, fontWeight: priority === p ? 700 : 400, color: priority === p ? prioColor[p] : C.muted, textTransform: "capitalize" }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Message</div>
              <textarea
                value={msg}
                onChange={e => setMsg(e.target.value)}
                placeholder="Describe the issue…"
                style={{ width: "100%", height: 90, padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, outline: "none", resize: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
            </div>
            {/* Delivery preview */}
            {toRole && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>Will be delivered via</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {["🔔 In-App", "✉️ Email", "💬 Teams", ...(priority === "critical" ? ["📱 SMS"] : [])].map(ch => (
                    <span key={ch} style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{ch}</span>
                  ))}
                </div>
              </div>
            )}
            <button
              onClick={submit}
              disabled={!toRole || !msg || sending}
              style={{ background: toRole && msg && !sending ? C.burgundy : "#ccc", color: "#fff", border: "none", borderRadius: 10, padding: "11px", fontWeight: 700, fontSize: 13, cursor: toRole && msg && !sending ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {sending ? (
                <>
                  <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid rgba(255,255,255,.3)", borderTop: "2px solid #fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                  Sending…
                </>
              ) : "Send Escalation"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── CECL SCENARIOS DETAIL ─────────────────────────────────────────
export function CECLScenariosDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="CECL Multi-Scenario Stress Test" onClose={onClose} accentColor={C.gold}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Base Provision", "$40.7M", C.text],
          ["Base ACL Rate", "1.42%", C.gold],
          ["Severe Scenario", "$112.8M", C.burgundy],
          ["Scenarios Modeled", "5", C.forest],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>Scenario Comparison</div>
          {CECL_SCENARIOS.map((s, i) => (
            <div key={i} style={{ padding: "13px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 80px 80px 1fr 80px", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{s.label}</div>
                <div style={{ fontSize: 11, color: C.muted }}>GDP: {s.gdp > 0 ? "+" : ""}{s.gdp}% · Unemployment: {s.unemployment}%</div>
              </div>
              <div style={{ fontSize: 12, color: s.hpiChange >= 0 ? C.forest : C.burgundy, fontWeight: 700 }}>
                HPI {s.hpiChange > 0 ? "+" : ""}{s.hpiChange}%
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: s.color }}>
                {(s.aclRate * 100).toFixed(2)}%
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: s.color }}>
                ${(s.provision / 1e6).toFixed(1)}M
              </div>
              <Bdg label={s.reserveStatus} color={s.reserveStatus === "Adequate" ? C.forest : s.reserveStatus === "Stressed" ? C.burgundy : C.gold} />
            </div>
          ))}
        </Card>
        <AiBox
          text="Under S3 Severe scenario, provision jumps to $112.8M — a $72.1M gap vs. current ACL of $40.7M. S5 Ag+CRE scenario is most relevant given River County drought and local MSA CRE vacancy trends. Recommend stress-testing Q-factors at ALCO before March close. Current capital levels can absorb S2 without breaching 8% CET1."
          c={C.gold}
          source="FASB ASC 326 · Moody's CRE Stress Scenarios · USDA Drought Monitor · Internal CECL Model"
        />
      </div>
    </DetailModal>
  );
}

// ── LENDING PIPELINE DETAIL ───────────────────────────────────────
export function LendingPipelineDetail({ onClose }: { onClose: () => void }) {
  const stageColor: Record<string, string> = {
    "Approved": C.forest, "Committee": C.gold, "Underwriting": C.rust,
    "Processing": C.gold, "Pre-approval": C.cyan, "Term Sheet": C.cyan, "SBA Review": C.muted,
  };
  const total = LENDING_PIPELINE.reduce((s, l) => s + l.amount, 0);
  const stalled = LENDING_PIPELINE.filter(l => l.issue !== null);
  return (
    <DetailModal title="Lending Pipeline Detail" onClose={onClose} accentColor={C.cyan}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Pipeline Total", `$${(total / 1e6).toFixed(1)}M`, C.text],
          ["Active Items", `${LENDING_PIPELINE.length}`, C.forest],
          ["Stalled / Issues", `${stalled.length}`, C.burgundy],
          ["Approved / Close", `${LENDING_PIPELINE.filter(l => l.stage === "Approved").length}`, C.forest],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>All Pipeline Items</div>
          {LENDING_PIPELINE.map((lp, i) => (
            <div key={i} style={{ padding: "13px 16px", borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "2fr 80px 1fr 1fr 80px", gap: 12, alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{lp.borrower}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{lp.id} · {lp.type} · {lp.rm}</div>
                {lp.issue && <div style={{ fontSize: 10, color: C.burgundy, fontWeight: 600, marginTop: 2 }}>⚠ {lp.issue}</div>}
              </div>
              <Bdg label={lp.stage} color={stageColor[lp.stage] || C.muted} />
              <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>${(lp.amount / 1e6).toFixed(1)}M</div>
              <div style={{ fontSize: 11, color: C.muted }}>Close: {lp.closeTarget}</div>
              <div style={{ fontSize: 11, color: lp.daysIn > 20 ? C.burgundy : lp.daysIn > 10 ? C.gold : C.muted }}>{lp.daysIn}d in stage</div>
            </div>
          ))}
        </Card>
        <AiBox
          text="3 of 8 pipeline items are stalled. LP-006 Martinez Trucking is approved but title search is blocking close — 8 days to Mar 10 target. LP-004 Central Valley Orchards needs borrowing base before 2 PM committee. LP-001 and LP-005 together push CRE concentration to 298% — ALCO pre-approval required before term sheets."
          c={C.cyan}
          source="Internal LOS · ALCO Policy · FFIEC CRE Concentration Guidance"
        />
      </div>
    </DetailModal>
  );
}

// ── CUSTOMER AT-RISK DETAIL ───────────────────────────────────────
export function CustomerAtRiskDetail({ onClose }: { onClose: () => void }) {
  const atRisk = BOOK_OF_BUSINESS.filter(c => {
    const profile = CUSTOMER_RISK_PROFILES[c.id];
    return profile && profile.atRiskScore >= 0.5;
  });
  return (
    <DetailModal title="Customers At Risk — Retention Priority" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["At-Risk Customers", `${atRisk.length}`, C.burgundy],
          ["At-Risk Revenue", "$218K", C.burgundy],
          ["Retention Rate YTD", "94%", C.forest],
          ["Avg Wellness Score", `${Math.round(atRisk.reduce((s, c) => s + (CUSTOMER_RISK_PROFILES[c.id]?.wellnessScore ?? 0), 0) / (atRisk.length || 1))}`, C.gold],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", background: C.surface, fontWeight: 700, fontSize: 13, color: C.text }}>At-Risk Relationship Detail</div>
          {atRisk.map((c, i) => {
            const profile = CUSTOMER_RISK_PROFILES[c.id];
            return (
              <div key={i} style={{ padding: "13px 16px", borderTop: `1px solid ${C.border}` }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 60px 1fr", gap: 12, alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{c.name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{c.type}</div>
                    <div style={{ fontSize: 11, color: C.burgundy, marginTop: 3 }}>{profile.signal}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: profile.wellnessScore > 80 ? C.forest : profile.wellnessScore > 65 ? C.gold : C.burgundy }}>{profile.wellnessScore}</div>
                    <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>Wellness</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: profile.atRiskScore >= 0.75 ? C.burgundy : C.gold }}>
                    {Math.round(profile.atRiskScore * 100)}% flight risk
                  </div>
                </div>
                {profile.wellnessFlags.length > 0 && (
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {profile.wellnessFlags.map((f, j) => (
                      <Bdg key={j} label={f} color={C.burgundy} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </Card>
        <AiBox
          text="Meridian Properties Group (88% flight risk) requires immediate attention — anchor tenant vacancy with NOI -22%. Valley Ridge Growers (79%) has LOC at 96% utilization with revenue -18% YoY. Apex Medical Group 48-hr retention window is closing — rate hold offer recommended today. Proactive outreach within 48 hours shows 3x higher retention rate."
          c={C.burgundy}
          source="Internal CRM · AFS Credit Platform · Competitive Intelligence Model"
        />
      </div>
    </DetailModal>
  );
}

// ── INVESTMENT PORTFOLIO DETAIL ───────────────────────────────────
export function InvestmentPortfolioDetail({ onClose }: { onClose: () => void }) {
  const ip = INVESTMENT_PORTFOLIO;
  return (
    <DetailModal title="Investment Portfolio — AFS / HTM Analysis" onClose={onClose} accentColor="#1E3A5F">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Securities", `$${(ip.totalSecurities/1e6).toFixed(0)}M`, C.text],
          ["AFS Unrealized Loss", `$(${Math.abs(ip.afs.unrealizedLoss/1e6).toFixed(1)})M`, C.burgundy],
          ["AOCI Impact", `$(${Math.abs(ip.afs.aoci/1e6).toFixed(1)})M`, C.burgundy],
          ["AFS Duration", `${ip.afs.duration}yr`, C.gold],
          ["HTM Fair Value Loss", `$(${Math.abs(ip.htm.fairValue - ip.htm.total)/1e6 < 0 ? 0 : Math.abs(ip.htm.total - ip.htm.fairValue)/1e6 }M)`, C.muted],
        ]} />

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Available-for-Sale (AFS) — ${(ip.afs.total/1e6).toFixed(0)}M</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 80px 60px 60px 90px 80px", padding: "9px 16px", background: C.surface, gap: 10 }}>
            {["Security Type","Balance","Duration","Coupon","Unrealized","Maturity"].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>
            ))}
          </div>
          {ip.afs.holdings.map((h, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 80px 60px 60px 90px 80px", padding: "11px 16px", borderTop: `1px solid ${C.border}`, gap: 10, alignItems: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{h.type}</div>
              <div style={{ fontSize: 12, color: C.text }}>${(h.bal/1e6).toFixed(0)}M</div>
              <div style={{ fontSize: 12, color: C.gold }}>{h.dur}yr</div>
              <div style={{ fontSize: 12, color: C.text }}>{h.coupon.toFixed(2)}%</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: h.unrealized < 0 ? C.burgundy : C.forest }}>{h.unrealized >= 0 ? "+" : ""}${(h.unrealized/1e6).toFixed(1)}M</div>
              <div style={{ fontSize: 11, color: C.muted }}>{h.matBucket}</div>
            </div>
          ))}
        </Card>

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Held-to-Maturity (HTM) — ${(ip.htm.total/1e6).toFixed(0)}M</div>
        <Card style={{ padding: 14 }}>
          <KVList rows={[
            ["Book Value", `$${(ip.htm.bookValue/1e6).toFixed(0)}M`, C.text],
            ["Fair Value", `$${(ip.htm.fairValue/1e6).toFixed(0)}M`, C.burgundy],
            ["Unrealized Loss (not in AOCI)", `$(${((ip.htm.bookValue - ip.htm.fairValue)/1e6).toFixed(1)})M — disclosed only`, C.burgundy],
            ["Duration", `${ip.htm.duration}yr`, C.gold],
            ["SVB Examiner Note", "HTM unrealized loss now disclosed in exam — OCC 2023-27", C.muted],
          ]} />
        </Card>

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Projected Cash Flow — Next 6 Months</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "9px 16px", background: C.surface, gap: 10 }}>
            {["Month","Principal","Coupon"].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>)}
          </div>
          {ip.cashFlow.map((cf, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "10px 16px", borderTop: `1px solid ${C.border}`, gap: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{cf.month}</div>
              <div style={{ fontSize: 12, color: C.text }}>${(cf.principal/1e6).toFixed(1)}M</div>
              <div style={{ fontSize: 12, color: C.forest }}>${(cf.coupon/1e3).toFixed(0)}K</div>
            </div>
          ))}
        </Card>

        <AiBox text="AFS AOCI of -$27.2M flows through Tier 1 under DFAST rules — directly compresses regulatory capital. At +200bps, the portfolio duration of 5.8yr implies an additional ~$14M mark-to-market decline. Recommend shortening duration on new purchases and using FHLB advances to fund loan growth rather than selling AFS at a loss. HTM unrealized loss of $22.6M is now a required exam disclosure per OCC 2023-27 — prepare CRO briefing for May exam." c="#1E3A5F" source="Bancware ALM · Bloomberg AFS Mark-to-Market · OCC Bulletin 2023-27 · FDIC FIL-2023-13" />
      </div>
    </DetailModal>
  );
}

// ── CD MATURITY LADDER DETAIL ─────────────────────────────────────
export function CDLadderDetail({ onClose }: { onClose: () => void }) {
  const cd = CD_MATURITY_LADDER;
  const repColor: Record<string,string> = { high: C.burgundy, medium: C.gold, low: C.forest };
  return (
    <DetailModal title="CD Maturity Ladder & Deposit Repricing" onClose={onClose} accentColor={C.gold}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Time Deposits", `$${(cd.totalTimeDeposits/1e6).toFixed(0)}M`, C.text],
          ["Avg Rate", `${cd.avgRate.toFixed(2)}%`, C.gold],
          ["Repricing < 6 Mo.", `$${((cd.buckets[0].amount + cd.buckets[1].amount)/1e6).toFixed(0)}M`, C.burgundy],
          ["Rate Exceptions", `${cd.rateExceptions.length}`, C.rust],
        ]} />

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Maturity Buckets</div>
        <Card style={{ padding: 14 }}>
          {cd.buckets.map((b, i) => (
            <div key={i} style={{ marginBottom: i < cd.buckets.length - 1 ? 14 : 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{b.label}</span>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>Avg: {b.rate.toFixed(2)}%</span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: repColor[b.repricingRisk] }}>${(b.amount/1e6).toFixed(0)}M</span>
                  <span style={{ fontSize: 10, color: C.dim, marginLeft: 6 }}>{b.pct}%</span>
                </div>
              </div>
              <Bar val={b.pct} max={35} color={repColor[b.repricingRisk]} />
            </div>
          ))}
        </Card>

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Rate Exceptions</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 80px 80px 80px 80px 80px", padding: "9px 16px", background: C.surface, gap: 10 }}>
            {["Customer","Amount","Approved","Ceiling","Exception","Approver"].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>
            ))}
          </div>
          {cd.rateExceptions.map((r, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 80px 80px 80px 80px 80px", padding: "10px 16px", borderTop: `1px solid ${C.border}`, gap: 10, alignItems: "center" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{r.customer}</div>
              <div style={{ fontSize: 11, color: C.muted }}>${(r.amount/1e6).toFixed(1)}M</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.gold }}>{r.approvedRate.toFixed(2)}%</div>
              <div style={{ fontSize: 11, color: C.muted }}>{r.ceilingRate.toFixed(2)}%</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.rust }}>{r.exception}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{r.approver}</div>
            </div>
          ))}
        </Card>
        <AiBox text="$140M in CDs reprices in under 6 months at an avg rate of 4.79%. If the Fed cuts 50bps by June, extending these deposits to 12–18 months NOW at 4.55% locks in carry and reduces runoff risk. Recommend a targeted retention campaign for the $48M maturing in under 3 months — current competitor floor is 4.85% for 12-month. Proactive retention saves an estimated $420K in replacement deposit cost." c={C.gold} source="FIS Horizon Deposit Tracking · FDIC Rate Comparability Tool · FHLB Rate Sheet" />
      </div>
    </DetailModal>
  );
}

// ── LCR / NSFR DETAIL ────────────────────────────────────────────
export function LCRDetail({ onClose }: { onClose: () => void }) {
  const lm = LIQUIDITY_METRICS;
  return (
    <DetailModal title="Liquidity Coverage Ratio (LCR) & Stress Analysis" onClose={onClose} accentColor="#1E3A5F">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["LCR (Base)", `${(lm.lcr * 100).toFixed(0)}%`, C.forest],
          ["NSFR", `${(lm.nsfr * 100).toFixed(0)}%`, C.forest],
          ["Total HQLA", `$${(lm.hqla.total/1e6).toFixed(0)}M`, C.text],
          ["30-day Net Outflow", `$${((lm.thirtyDayOutflows.total - lm.thirtyDayInflows.total)/1e6).toFixed(0)}M`, C.burgundy],
        ]} />

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>High Quality Liquid Assets (HQLA)</div>
        <Card style={{ padding: 14 }}>
          <KVList rows={[
            ["Level 1 — Cash + Fed Reserves", `$${(lm.hqla.l1/1e6).toFixed(0)}M`, C.forest],
            ["Level 2A — Agency Securities (95%)", `$${(lm.hqla.l2a/1e6).toFixed(0)}M`, C.forest],
            ["Level 2B — Other Eligible AFS (50%)", `$${(lm.hqla.l2b/1e6).toFixed(0)}M`, C.gold],
            ["Total HQLA", `$${(lm.hqla.total/1e6).toFixed(0)}M`, C.text],
          ]} />
        </Card>

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Stress Scenario Analysis</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {lm.stressOutflows.map((s, i) => (
            <div key={i} style={{ padding: "13px 16px", borderBottom: i < lm.stressOutflows.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{s.scenario}</span>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: C.muted }}>Outflow: ${(s.outflow/1e6).toFixed(0)}M</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: s.coverage >= 1.0 ? C.forest : C.burgundy }}>LCR: {(s.coverage * 100).toFixed(0)}%</span>
                </div>
              </div>
              <Bar val={Math.min(s.coverage * 100, 200)} max={200} color={s.coverage >= 1.0 ? C.forest : C.burgundy} />
            </div>
          ))}
        </Card>

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Contingency Funding Capacity</div>
        <Card style={{ padding: 14 }}>
          {lm.contingencyCapacity.map((c, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < lm.contingencyCapacity.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <span style={{ fontSize: 12, color: C.muted }}>{c.source}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.forest }}>${(c.amount/1e6).toFixed(0)}M</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontWeight: 800 }}>
            <span style={{ fontSize: 13, color: C.text }}>Total Contingency Capacity</span>
            <span style={{ fontSize: 14, color: C.text }}>${(lm.contingencyCapacity.reduce((s, c) => s + c.amount, 0)/1e6).toFixed(0)}M</span>
          </div>
        </Card>
        <AiBox text="Base LCR of 182% is strong, but the SVB-parallel stress scenario (run-on-bank) drops coverage to 61%. The critical vulnerability is the $502M in combined top-3 depositor balances which are operationally concentrated. Recommend: (1) FHLB pre-positioning $100M additional before FOMC, (2) IntraFi CDARS placement for Metro Motors $120M to reduce uninsured exposure, (3) Board-approved contingency funding plan update before May exam." c="#1E3A5F" source="FDIC FIL-13-2023 · Bancware Liquidity Stress Model · FHLB Collateral Report" />
      </div>
    </DetailModal>
  );
}

// ── LOAN FILE DETAIL ──────────────────────────────────────────────
export function LoanFileDetail({ onClose, creditId }: { onClose: () => void; creditId?: string }) {
  const [selected, setSelected] = useState(creditId || WATCHLIST_CREDITS[0].id);
  const credit = WATCHLIST_CREDITS.find(c => c.id === selected) || WATCHLIST_CREDITS[0];
  const clsColor: Record<string,string> = { "Special Mention": C.gold, Substandard: C.burgundy, Doubtful: "#8B0000", Pass: C.forest };

  return (
    <DetailModal title="Loan File Navigator" onClose={onClose} accentColor={C.navy}>
      <div style={{ display: "flex", gap: 16 }}>
        {/* Sidebar */}
        <div style={{ width: 180, flexShrink: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Watchlist Credits</div>
          {WATCHLIST_CREDITS.map(c => (
            <button key={c.id} onClick={() => setSelected(c.id)} style={{ width: "100%", textAlign: "left", padding: "10px 12px", borderRadius: 8, border: `1px solid ${selected === c.id ? clsColor[c.cls] : C.border}`, background: selected === c.id ? `${clsColor[c.cls]}12` : "transparent", cursor: "pointer", marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 2 }}>{c.name}</div>
              <div style={{ fontSize: 10, color: clsColor[c.cls] || C.muted, fontWeight: 700 }}>{c.cls}</div>
              <div style={{ fontSize: 10, color: C.dim }}>${(c.bal/1e6).toFixed(1)}M</div>
            </button>
          ))}
        </div>
        {/* Detail */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{credit.name}</div>
              <div style={{ fontSize: 12, color: C.muted }}>{credit.type} · {credit.rm} · {credit.id}</div>
            </div>
            <Bdg label={credit.cls} color={clsColor[credit.cls] || C.muted} />
          </div>
          <StatRow stats={[
            ["Balance", `$${(credit.bal/1e6).toFixed(2)}M`, C.text],
            ["DSCR", credit.dscr?.toFixed(2) ?? "N/A", credit.dscr && credit.dscr < 1.2 ? C.burgundy : C.forest],
            ["LTV", credit.ltv ? `${(credit.ltv*100).toFixed(0)}%` : "N/A", credit.ltv && credit.ltv > 0.70 ? C.gold : C.forest],
            ["Maturity", credit.maturity || "N/A", C.gold],
          ]} />
          <Card style={{ padding: 14, background: C.surface }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Credit Memo</div>
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.8, margin: 0 }}>{credit.creditMemo}</p>
          </Card>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5 }}>Action Log</div>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {credit.actions.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "11px 14px", borderBottom: i < credit.actions.length - 1 ? `1px solid ${C.border}` : "none", alignItems: "flex-start" }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: a.done ? C.forest : C.surface, border: `2px solid ${a.done ? C.forest : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  {a.done && <span style={{ color: "#fff", fontSize: 10 }}>✓</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: a.done ? C.muted : C.text, textDecoration: a.done ? "line-through" : "none" }}>{a.action}</div>
                  <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>{a.date} · {a.user}</div>
                </div>
              </div>
            ))}
          </Card>
          <KVList rows={[
            ["Collateral", credit.collateral, C.muted],
            ["Rate", `${credit.rate}%`, C.text],
            ["Occupancy", credit.occupancy ? `${(credit.occupancy * 100).toFixed(0)}%` : "N/A", credit.occupancy && credit.occupancy < 0.80 ? C.burgundy : C.forest],
            ["Committee Status", credit.committeeStatus, credit.committeeStatus === "Pending" ? C.gold : C.forest],
            ["Committee Date", credit.committeeDate, C.text],
          ]} />
        </div>
      </div>
    </DetailModal>
  );
}

// ── WATCHLIST COMMITTEE DETAIL ────────────────────────────────────
export function WatchlistCommitteeDetail({ onClose }: { onClose: () => void }) {
  const [signoffs, setSignoffs] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const clsColor: Record<string,string> = { "Special Mention": C.gold, Substandard: C.burgundy, Doubtful: "#8B0000" };

  function signOff(id: string) {
    setSignoffs(s => ({ ...s, [id]: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) }));
  }

  return (
    <DetailModal title="Watchlist Committee Workflow" onClose={onClose} accentColor={C.burgundy}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <StatRow stats={[
          ["Credits on Watchlist", `${WATCHLIST_CREDITS.length}`, C.text],
          ["Pending Committee", `${WATCHLIST_CREDITS.filter(c => c.committeeStatus === "Pending").length}`, C.burgundy],
          ["Signed Off", `${Object.keys(signoffs).length}`, C.forest],
          ["Total Exposure", `$${(WATCHLIST_CREDITS.reduce((s, c) => s + c.bal, 0)/1e6).toFixed(1)}M`, C.gold],
        ]} />
        {WATCHLIST_CREDITS.map(credit => (
          <Card key={credit.id} style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", cursor: "pointer", background: signoffs[credit.id] ? `${C.forest}08` : "transparent" }} onClick={() => setExpanded(expanded === credit.id ? null : credit.id)}>
              <div style={{ width: 3, height: 40, background: clsColor[credit.cls] || C.muted, flexShrink: 0, borderRadius: 4 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{credit.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{credit.type} · ${(credit.bal/1e6).toFixed(1)}M · RM: {credit.rm}</div>
              </div>
              <Bdg label={credit.cls} color={clsColor[credit.cls] || C.muted} />
              <Bdg label={signoffs[credit.id] ? `Signed ${signoffs[credit.id]}` : credit.committeeStatus} color={signoffs[credit.id] ? C.forest : credit.committeeStatus === "Pending" ? C.gold : C.muted} />
              <span style={{ fontSize: 12, color: C.muted }}>{expanded === credit.id ? "▲" : "▼"}</span>
            </div>
            {expanded === credit.id && (
              <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.7, marginTop: 12, marginBottom: 12 }}>{credit.creditMemo}</p>
                <KVList rows={[
                  ["Last Review", credit.lastReview, C.muted],
                  ["Next Review", credit.nextReview, C.text],
                  ["DSCR", credit.dscr?.toFixed(2) ?? "N/A", credit.dscr && credit.dscr < 1.2 ? C.burgundy : C.forest],
                  ["LTV", credit.ltv ? `${(credit.ltv*100).toFixed(0)}%` : "N/A", C.text],
                  ["Committee Date", credit.committeeDate, C.gold],
                ]} />
                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button onClick={() => signOff(credit.id)} disabled={!!signoffs[credit.id]} style={{ flex: 1, padding: "10px", borderRadius: 9, border: `1px solid ${signoffs[credit.id] ? C.border : C.forest}`, background: signoffs[credit.id] ? C.surface : `${C.forest}15`, color: signoffs[credit.id] ? C.muted : C.forest, cursor: signoffs[credit.id] ? "default" : "pointer", fontWeight: 700, fontSize: 12 }}>
                    {signoffs[credit.id] ? `✓ Signed off at ${signoffs[credit.id]}` : "✓ Sign Off — Watchlist Confirmed"}
                  </button>
                  <button style={{ padding: "10px 16px", borderRadius: 9, border: `1px solid ${C.burgundy}`, background: `${C.burgundy}12`, color: C.burgundy, cursor: "pointer", fontWeight: 700, fontSize: 12 }}>
                    ↑ Classify Adverse
                  </button>
                </div>
              </div>
            )}
          </Card>
        ))}
        <AiBox text="Meridian Properties Group and Selma Retail Partners both have declining DSCR with upcoming maturities — adverse classification memo should be prepared for committee consideration. Valley Ridge Growers' LOC utilization pattern and drought exposure suggest Substandard migration with 84% model confidence. All three credits should have updated operating statements before committee sign-off." c={C.burgundy} source="AFS Credit Platform · FDIC ARIS · Moody's Migration Analytics" />
      </div>
    </DetailModal>
  );
}

// ── LOAN MODIFICATION DETAIL ──────────────────────────────────────
export function LoanModificationDetail({ onClose }: { onClose: () => void }) {
  return (
    <DetailModal title="Loan Modifications & Forbearance Tracker" onClose={onClose} accentColor={C.rust}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <StatRow stats={[
          ["Active Modifications", `${LOAN_MODIFICATIONS.length}`, C.text],
          ["TDR Loans", `${LOAN_MODIFICATIONS.filter(m => m.tdr).length}`, C.burgundy],
          ["Total Balance", `$${(LOAN_MODIFICATIONS.reduce((s,m)=>s+m.bal,0)/1e6).toFixed(1)}M`, C.gold],
        ]} />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 80px 1fr 90px 70px 80px", padding: "9px 16px", background: C.surface, gap: 10 }}>
            {["Borrower","Balance","Mod Type","Expiry","TDR","Status"].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>
            ))}
          </div>
          {LOAN_MODIFICATIONS.map((m, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 80px 1fr 90px 70px 80px", padding: "12px 16px", borderTop: `1px solid ${C.border}`, gap: 10, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{m.borrower}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{m.type} · Rate: {m.origRate}% → {m.modRate}%</div>
                <div style={{ fontSize: 10, color: C.dim, marginTop: 1 }}>{m.reason}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(m.bal/1e6).toFixed(1)}M</div>
              <div style={{ fontSize: 11, color: C.text }}>{m.modType}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{m.expiryDate}</div>
              <div><Bdg label={m.tdr ? "TDR" : "Non-TDR"} color={m.tdr ? C.burgundy : C.muted} /></div>
              <div><Bdg label={m.status} color={m.status === "Active" ? C.gold : C.forest} /></div>
            </div>
          ))}
        </Card>
        <AiBox text="2 of 4 modifications are TDRs under FASB ASC 310-40 — these require additional ACL reserves and disclosure in Call Report RC-C. Sunrise Storage LLC (Doubtful) and Valley Auto Parts (Substandard) should be reviewed for charge-off timing before May FDIC exam. Martinez Trucking payment deferral expires May 31 — assess capacity to resume before expiry." c={C.rust} source="FASB ASC 310-40 TDR Guidance · FDIC Call Report RC-C · AFS Credit Platform" />
      </div>
    </DetailModal>
  );
}

// ── AG CROP CONCENTRATION DETAIL ──────────────────────────────────
export function AgCropConcentrationDetail({ onClose }: { onClose: () => void }) {
  const impactColor: Record<string,string> = { high: C.burgundy, medium: C.gold, low: C.forest };
  return (
    <DetailModal title="Agricultural Portfolio — Crop-by-Crop Concentration" onClose={onClose} accentColor={C.forest}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <StatRow stats={[
          ["Total Ag Portfolio", "$438M", C.text],
          ["Almonds (largest)", "$168M", C.gold],
          ["Insurance Gaps", `${AG_CROP_CONCENTRATION.reduce((s,c)=>s+(c.insuranceTotal-c.insuranceVerified),0)}`, C.burgundy],
          ["Drought High Risk", `${AG_CROP_CONCENTRATION.filter(c=>c.droughtImpact==="high").reduce((s,c)=>s+c.balance,0)/1e6 | 0}M`, C.burgundy],
        ]} />
        {AG_CROP_CONCENTRATION.map((c, i) => (
          <Card key={i} style={{ padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{c.crop}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{c.accounts} accounts · Avg LTV {(c.avgLTV*100).toFixed(0)}%</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: impactColor[c.droughtImpact] }}>${(c.balance/1e6).toFixed(0)}M</div>
                <div style={{ fontSize: 10, color: C.dim }}>{(c.pct*100).toFixed(1)}% of portfolio</div>
              </div>
            </div>
            <Bar val={c.pct * 100} max={40} color={impactColor[c.droughtImpact]} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>Drought Impact</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: impactColor[c.droughtImpact], textTransform: "capitalize" }}>{c.droughtImpact}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>Yield Risk</div>
                <div style={{ fontSize: 11, color: C.muted }}>{c.yieldRisk}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>Insurance</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: c.insuranceVerified === c.insuranceTotal ? C.forest : C.burgundy }}>{c.insuranceVerified}/{c.insuranceTotal} verified</div>
              </div>
            </div>
          </Card>
        ))}
        <AiBox text="Almonds ($168M) and Pistachios ($94M) together represent 60% of the ag portfolio and face 20–35% yield reductions under the current D3 Extreme Drought designation. These crops have highly correlated drought risk — a single River County drought event simultaneously impairs both. Recommend: separate watch list by crop type, increase Ag Q-factor by $600K for almond and pistachio exposure, and require crop insurance verification for all accounts over $2M before April 30." c={C.forest} source="USDA Drought Monitor D3 Feb 2026 · USDA NASS Yield Data · RMA Crop Insurance Database" />
      </div>
    </DetailModal>
  );
}

// ── MORTGAGE PIPELINE DETAIL ──────────────────────────────────────
export function MortgagePipelineDetail({ onClose }: { onClose: () => void }) {
  const [filter, setFilter] = useState("All");
  const statusColor: Record<string,string> = { Approved: C.forest, Underwriting: C.gold, Processing: C.gold, Conditional: C.gold, "Docs Ordered": C.forest, Suspended: C.burgundy };
  const filtered = filter === "All" ? MORTGAGE_PIPELINE : MORTGAGE_PIPELINE.filter(m => m.purpose === filter || m.status === filter);
  const totalAmt = MORTGAGE_PIPELINE.reduce((s,m) => s+m.amount, 0);

  return (
    <DetailModal title="Mortgage & Consumer Lending Pipeline" onClose={onClose} accentColor="#6B3D8A">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <StatRow stats={[
          ["Pipeline Total", `$${(totalAmt/1e6).toFixed(1)}M`, C.text],
          ["Purchase", `${MORTGAGE_PIPELINE.filter(m=>m.purpose==="Purchase").length}`, C.forest],
          ["Refi", `${MORTGAGE_PIPELINE.filter(m=>m.purpose==="Refi").length}`, "#6B3D8A"],
          ["Expiring ≤14d", `${MORTGAGE_PIPELINE.filter(m=>m.daysToLock<=14).length}`, C.burgundy],
          ["LMI/CRA", `${MORTGAGE_PIPELINE.filter(m=>m.lmi).length}`, C.forest],
        ]} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["All","Purchase","Refi","Approved","Suspended"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ fontSize: 10, padding: "4px 10px", borderRadius: 20, border: `1px solid ${filter===f ? "#6B3D8A" : C.border}`, background: filter===f ? "#6B3D8A15" : "transparent", color: filter===f ? "#6B3D8A" : C.muted, cursor: "pointer", fontWeight: filter===f ? 700 : 400 }}>{f}</button>
          ))}
        </div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "60px 2fr 1fr 80px 60px 60px 70px 80px", padding: "9px 14px", background: C.surface, gap: 10 }}>
            {["ID","Borrower","Type","Amount","LTV","FICO","Lock","Status"].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>
            ))}
          </div>
          {filtered.map((m, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "60px 2fr 1fr 80px 60px 60px 70px 80px", padding: "11px 14px", borderTop: `1px solid ${C.border}`, gap: 10, alignItems: "center", background: m.status === "Suspended" ? `${C.burgundy}06` : "transparent" }}>
              <div style={{ fontSize: 10, color: C.dim }}>{m.id}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{m.borrower}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{m.purpose}{m.lmi ? " · CRA/LMI" : ""}</div>
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>{m.type}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(m.amount/1e3).toFixed(0)}K</div>
              <div style={{ fontSize: 11, color: m.ltv > 0.95 ? C.burgundy : C.muted }}>{(m.ltv*100).toFixed(0)}%</div>
              <div style={{ fontSize: 11, color: m.fico < 660 ? C.burgundy : m.fico < 700 ? C.gold : C.muted }}>{m.fico}</div>
              <div style={{ fontSize: 11, color: m.daysToLock <= 14 ? C.burgundy : m.daysToLock <= 21 ? C.gold : C.muted, fontWeight: m.daysToLock <= 14 ? 700 : 400 }}>{m.daysToLock}d</div>
              <Bdg label={m.status} color={statusColor[m.status] || C.muted} />
            </div>
          ))}
        </Card>
        <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>Consumer Pipeline</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {CONSUMER_LOANS.map((c, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "11px 14px", borderBottom: i < CONSUMER_LOANS.length-1 ? `1px solid ${C.border}` : "none", alignItems: "center" }}>
              <Bdg label={c.type} color="#6B3D8A" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{c.borrower}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{c.issue || `${c.term}mo · ${c.rate}% APR · FICO ${c.fico}`}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(c.amount/1e3).toFixed(0)}K</div>
              <Bdg label={c.status} color={c.status==="Approved"||c.status==="Closing" ? C.forest : c.status==="Declined" ? C.burgundy : C.gold} />
            </div>
          ))}
        </Card>
        <AiBox text="MG-007 Williams suspension requires immediate adverse action today (ECOA 30-day rule). MG-001 Rodriguez lock expires Mar 15 — two UW conditions open. HMDA LAR has 2 errors correctable before March 31. Fair lending alert: denial rate for Hispanic surnames in local MSA is 2.1% above peer median — ensure consistent UW documentation across all files." c="#6B3D8A" source="CFPB HMDA Reg C · ECOA Adverse Action Rules · FFIEC Fair Lending Exam Procedures" />
      </div>
    </DetailModal>
  );
}

// ── WEALTH / TRUST AUM DETAIL ─────────────────────────────────────
export function WealthAUMDetail({ onClose }: { onClose: () => void }) {
  const w = WEALTH_DATA;
  const statusColor: Record<string,string> = { Current: C.forest, "At Risk": C.burgundy, Overdue: C.gold };
  const prioColor: Record<string,string> = { critical: C.burgundy, high: C.gold, medium: C.rust };

  return (
    <DetailModal title="Wealth & Trust — AUM and Fiduciary Management" onClose={onClose} accentColor="#2C6B5F">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <StatRow stats={[
          ["Total AUM", `$${(w.aum/1e6).toFixed(0)}M`, "#2C6B5F"],
          ["QoQ Growth", `+${(w.aumQoQChange*100).toFixed(1)}%`, C.forest],
          ["Annual Fee Pace", `$${(w.feeIncome/1e6).toFixed(2)}M`, C.text],
          ["Accounts", `${w.accounts}`, C.text],
          ["Fiduciary Alerts", `${w.fiduciaryAlerts.length}`, C.burgundy],
        ]} />

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Fiduciary Alerts — Action Required</div>
        {w.fiduciaryAlerts.map((a, i) => (
          <Card key={i} style={{ padding: 0, overflow: "hidden", border: `1px solid ${prioColor[a.priority]}30` }}>
            <div style={{ display: "flex" }}>
              <div style={{ width: 3, background: prioColor[a.priority], flexShrink: 0 }} />
              <div style={{ padding: "12px 14px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{a.type}</span>
                  <Bdg label={a.priority} color={prioColor[a.priority]} />
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{a.account}</div>
                {a.detail && <div style={{ fontSize: 11, color: C.dim }}>{a.detail}</div>}
              </div>
            </div>
          </Card>
        ))}

        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Account List</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 80px 80px 80px", padding: "9px 14px", background: C.surface, gap: 10 }}>
            {["Account","Type","AUM","Fee YTD","Next Review","Status"].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .4 }}>{h}</div>
            ))}
          </div>
          {w.accounts_list.map((acc, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 80px 80px 80px", padding: "11px 14px", borderTop: `1px solid ${C.border}`, gap: 10, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{acc.name}</div>
                <div style={{ fontSize: 10, color: C.muted }}>CM: {acc.cm} · {acc.riskProfile}</div>
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>{acc.type}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(acc.aum/1e6).toFixed(1)}M</div>
              <div style={{ fontSize: 11, color: C.forest }}>${(acc.feeYTD/1e3).toFixed(0)}K</div>
              <div style={{ fontSize: 11, color: C.muted }}>{acc.nextReview}</div>
              <Bdg label={acc.status} color={statusColor[acc.status] || C.muted} />
            </div>
          ))}
        </Card>
        <AiBox text="Regional Medical Pension equity IPS breach requires immediate rebalancing — every additional day of breach increases ERISA Section 404 fiduciary liability. Lakewood Trust annual review is 12 days overdue — document any communication delays as part of the compliance record. Fee schedule mismatch on Inland Ag Holdings ($5,720 overbilled) should be proactively corrected before client's next quarterly review in late March." c="#2C6B5F" source="ERISA Section 404 · OCC Trust Manual 12 CFR Part 9 · Uniform Principal and Income Act" />
      </div>
    </DetailModal>
  );
}

// ── BRANCH DRILLDOWN DETAIL ───────────────────────────────────────
export function BranchDrilldownDetail({ onClose }: { onClose: () => void }) {
  const [selected, setSelected] = useState("B01");
  const branch = BRANCH_DATA.find(b => b.id === selected) || BRANCH_DATA[0];

  return (
    <DetailModal title="Branch Performance — Drill-Down" onClose={onClose} accentColor="#7A4F1D">
      <div style={{ display: "flex", gap: 16 }}>
        {/* Branch selector sidebar */}
        <div style={{ width: 160, flexShrink: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8 }}>Select Branch</div>
          {BRANCH_DATA.map(b => (
            <button key={b.id} onClick={() => setSelected(b.id)} style={{ width: "100%", textAlign: "left", padding: "10px 12px", borderRadius: 8, border: `1px solid ${selected===b.id ? "#7A4F1D" : C.border}`, background: selected===b.id ? "#7A4F1D12" : "transparent", cursor: "pointer", marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{b.name}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{b.city}</div>
              <div style={{ fontSize: 11, fontWeight: 800, color: b.healthScore >= 85 ? C.forest : b.healthScore >= 70 ? C.gold : C.burgundy }}>{b.healthScore}/100</div>
            </button>
          ))}
        </div>
        {/* Branch detail */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{branch.name}</div>
            <div style={{ fontSize: 12, color: C.muted }}>{branch.city} · Manager: {branch.mgr}</div>
          </div>
          <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            {[
              { l: "Health Score", v: `${branch.healthScore}/100`, c: branch.healthScore >= 85 ? C.forest : branch.healthScore >= 70 ? C.gold : C.burgundy },
              { l: "Deposits", v: `$${(branch.deposits/1e6).toFixed(0)}M`, c: C.text },
              { l: "Loans", v: `$${(branch.loans/1e6).toFixed(0)}M`, c: C.text },
              { l: "Revenue/Teller", v: `$${branch.revenuePerTeller}`, c: C.forest },
              { l: "Tx/Teller/Month", v: branch.txPerTeller.toLocaleString(), c: C.text },
              { l: "Error Rate", v: `${(branch.errorRate*100).toFixed(3)}%`, c: branch.errorRate < 0.0003 ? C.forest : branch.errorRate < 0.0005 ? C.gold : C.burgundy },
            ].map((m, i) => (
              <Card key={i} style={{ padding: "12px 14px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>{m.l}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: m.c }}>{m.v}</div>
              </Card>
            ))}
          </div>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Goal vs. Actual</div>
          <Card style={{ padding: 14 }}>
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 12, color: C.muted }}>Deposit Goal</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>${(branch.deposits/1e6).toFixed(0)}M / ${(branch.goal/1e6).toFixed(0)}M</span>
              </div>
              <Bar val={(branch.deposits / branch.goal) * 100} max={100} color={(branch.deposits / branch.goal) >= 1 ? C.forest : (branch.deposits / branch.goal) >= 0.90 ? C.gold : C.burgundy} />
            </div>
            <KVList rows={[
              ["New DDA YTD", `${branch.ytdNewDDA} accounts`, C.text],
              ["New Loans YTD", `${branch.ytdNewLoans} loans`, C.text],
              ["Cost Per $1 Revenue", `$${branch.costPerDollar.toFixed(2)}`, branch.costPerDollar <= 0.23 ? C.forest : branch.costPerDollar <= 0.27 ? C.gold : C.burgundy],
              ["Teller Count", `${branch.tellers}`, C.text],
            ]} />
          </Card>
          <AiBox text={`${branch.name} health score of ${branch.healthScore} is ${branch.healthScore >= 85 ? "above network average — this branch should be used as the benchmark for peer coaching" : branch.healthScore >= 70 ? "within acceptable range but below Commerce Branch's leading 94 score — focus on transaction complexity mix and cross-sell" : "below network floor — immediate performance review and manager coaching plan recommended"}. Cost per dollar of revenue at $${branch.costPerDollar.toFixed(2)} ${branch.costPerDollar <= 0.23 ? "is best-in-class" : branch.costPerDollar >= 0.30 ? "is above the $0.27 threshold — consolidation analysis warranted" : "is near target"}.`} c="#7A4F1D" source="FIS Branch Profitability Module · Workday Teller Productivity · Internal Goal Sheet Q1 2026" />
        </div>
      </div>
    </DetailModal>
  );
}

// ── DEPOSIT PROFITABILITY DETAIL ───────────────────────────────────
export function DepositProfitabilityDetail({ onClose }: { onClose: () => void }) {
  const profColor = (p: string) => p === "high" ? C.forest : p === "medium" ? C.gold : C.burgundy;
  const totalAnnualProfit = DEPOSIT_PROFITABILITY.segments.reduce((s, x) => s + x.annualProfit, 0);
  return (
    <DetailModal title="Deposit Profitability Analysis" onClose={onClose} accentColor="#2C5F6A">
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <StatRow stats={[
          ["Total Deposits", "$3.62B", C.text],
          ["Avg Cost of Funds", `${(DEPOSIT_PROFITABILITY.avgCostOfFunds*100).toFixed(2)}%`, C.burgundy],
          ["Fully Loaded COF", `${(DEPOSIT_PROFITABILITY.fullyLoadedCOF*100).toFixed(2)}%`, C.gold],
          ["Est. Annual Profit", `$${(totalAnnualProfit/1e6).toFixed(1)}M`, C.forest],
        ]} />
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Profitability by Deposit Segment</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {["Segment","Balance","Rate","Serv. Cost","Net Spread","Annual Profit","Rating"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DEPOSIT_PROFITABILITY.segments.map((s, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : C.surface }}>
                  <td style={{ padding: "9px 12px", fontWeight: 600, color: C.text }}>{s.name}</td>
                  <td style={{ padding: "9px 12px", color: C.muted }}>${(s.balance/1e6).toFixed(0)}M</td>
                  <td style={{ padding: "9px 12px", color: C.muted }}>{(s.rate*100).toFixed(2)}%</td>
                  <td style={{ padding: "9px 12px", color: C.muted }}>{(s.servCost*100).toFixed(2)}%</td>
                  <td style={{ padding: "9px 12px", fontWeight: 700, color: s.netSpread >= 0 ? C.forest : C.burgundy }}>{s.netSpread >= 0 ? "+" : ""}{(s.netSpread*100).toFixed(2)}%</td>
                  <td style={{ padding: "9px 12px", fontWeight: 700, color: s.annualProfit >= 0 ? C.forest : C.burgundy }}>{s.annualProfit >= 0 ? "+$" : "-$"}{Math.abs(s.annualProfit/1e3).toFixed(0)}K</td>
                  <td style={{ padding: "9px 12px" }}><Bdg label={s.profitability.toUpperCase()} c={profColor(s.profitability)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Top Relationship Profitability</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {DEPOSIT_PROFITABILITY.topRelationships.map((r, i) => (
            <Card key={i} style={{ padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{r.name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{r.type} · {r.products} products · ${(r.balance/1e6).toFixed(0)}M balance</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: r.annualProfit > 100000 ? C.forest : C.gold }}>${(r.annualProfit/1e3).toFixed(0)}K/yr</div>
                  <div style={{ fontSize: 10, color: C.muted }}>CoF {(r.rate*100).toFixed(2)}% · ECR {(r.ecr*100).toFixed(2)}%</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: r.risk === "critical" ? C.burgundy : r.risk === "medium" ? C.gold : C.muted, fontStyle: "italic" }}>⚠ {r.profCheck}</div>
            </Card>
          ))}
        </div>
        <AiBox text="Non-interest DDA and Commercial DDA generate 78% of total deposit profit despite representing only 43% of balances. CD segment is marginally unprofitable at current rates (-$162K/yr). Metro Motors' rate exception (+25bps) reduces their net contribution by ~$71K annually — review exception policy on renewal. Recommend targeting 15% growth in Commercial DDA to offset NIM compression." c="#2C5F6A" source="FIS Horizon Deposit Analytics · Internal Transfer Pricing Model · Competitor Rate Survey Mar 2026" />
      </div>
    </DetailModal>
  );
}

// ── HMDA LAR DETAIL ────────────────────────────────────────────────
export function HMDALARDetail({ onClose }: { onClose: () => void }) {
  const [filter, setFilter] = useState("All");
  const actions = ["All", "Originated", "Denied", "Withdrawn"];
  const filtered = filter === "All" ? HMDA_LAR : HMDA_LAR.filter(r => r.action === filter);
  const errors = HMDA_LAR.filter(r => r.flag).length;
  return (
    <DetailModal title="HMDA LAR — Q1 2026 Loan Application Register" onClose={onClose} accentColor="#6B3D8A">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Applications", `${HMDA_LAR.length}`, C.text],
          ["Originated", `${HMDA_LAR.filter(r=>r.action==="Originated").length}`, C.forest],
          ["Denied", `${HMDA_LAR.filter(r=>r.action==="Denied").length}`, C.burgundy],
          ["LAR Errors", `${errors}`, errors > 0 ? C.burgundy : C.forest],
        ]} />
        {errors > 0 && (
          <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B", borderRadius: 8, padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: "#92400E", marginBottom: 4 }}>⚠ {errors} LAR Record(s) Require Correction Before March 31 Submission</div>
            {HMDA_LAR.filter(r=>r.flag).map((r,i) => (
              <div key={i} style={{ fontSize: 11, color: "#92400E" }}>{r.id}: {r.flag}</div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 6 }}>
          {actions.map(a => (
            <button key={a} onClick={() => setFilter(a)} style={{ fontSize: 11, padding: "5px 12px", borderRadius: 20, border: `1px solid ${filter===a ? C.burgundy : C.border}`, background: filter===a ? C.burgundyBg : "transparent", color: filter===a ? C.burgundy : C.muted, cursor: "pointer", fontWeight: filter===a ? 700 : 400 }}>{a}</button>
          ))}
        </div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {["LAR ID","Applicant","Race/Ethnicity","Action","Amount","LMI","Flag"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}`, background: r.flag ? "#FEF3C7" : i % 2 === 0 ? "transparent" : C.surface }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: C.muted, fontSize: 10 }}>{r.id}</td>
                  <td style={{ padding: "8px 10px", color: C.text, fontWeight: 600 }}>{r.applicant}</td>
                  <td style={{ padding: "8px 10px", color: C.muted }}>{r.race}</td>
                  <td style={{ padding: "8px 10px" }}><Bdg label={r.action} c={r.action==="Originated"?C.forest:r.action==="Denied"?C.burgundy:C.gold} /></td>
                  <td style={{ padding: "8px 10px", color: C.muted }}>${(r.loanAmt/1e3).toFixed(0)}K</td>
                  <td style={{ padding: "8px 10px" }}>{r.lmi ? <Bdg label="LMI" c={C.forest} /> : <span style={{color:C.dim}}>—</span>}</td>
                  <td style={{ padding: "8px 10px", fontSize: 10, color: "#92400E", maxWidth: 200 }}>{r.flag ? "⚠ "+r.flag.substring(0,40)+"…" : <span style={{color:C.dim}}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <AiBox text="LAR-007 (Williams) requires immediate correction: CFPB Reg C requires 3 separate denial reason codes when multiple factors apply. Single-reason coding is non-compliant. LAR-003 (Washington) income calculation must gross up non-taxable Social Security income per CFPB guidance. Both corrections are achievable before the March 31 LAR submission deadline." c="#6B3D8A" source="CFPB HMDA Regulation C · FFIEC HMDA Check Software · Internal LAR File" />
      </div>
    </DetailModal>
  );
}

// ── FAIR LENDING ANALYTICS DETAIL ─────────────────────────────────
export function FairLendingDetail({ onClose }: { onClose: () => void }) {
  const findings = FAIR_LENDING_DATA.examFindings;
  const sevColor = (s: string) => s === "finding" ? C.burgundy : s === "monitor" ? C.gold : C.muted;
  return (
    <DetailModal title="Fair Lending Analytics — ECOA / FHA / HMDA" onClose={onClose} accentColor="#2C6B5F">
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <StatRow stats={[
          ["Report Period", "Q1 2026", C.text],
          ["Total Applications", `${FAIR_LENDING_DATA.totalApplications}`, C.text],
          ["Exam Findings", `${findings.filter(f=>f.severity==="finding").length}`, findings.filter(f=>f.severity==="finding").length > 0 ? C.burgundy : C.forest],
          ["LMI Goal Progress", `${(FAIR_LENDING_DATA.lmiTracking.lmiPct*100).toFixed(1)}% / ${(FAIR_LENDING_DATA.lmiTracking.goalPct*100).toFixed(1)}%`, C.gold],
        ]} />
        {findings.filter(f=>f.severity==="finding").length > 0 && (
          <div style={{ background: "#FEF2F2", border: `1px solid ${C.burgundy}`, borderRadius: 8, padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: C.burgundy, marginBottom: 6 }}>Regulatory Findings Requiring Action</div>
            {findings.filter(f=>f.severity==="finding").map((f,i) => (
              <div key={i} style={{ fontSize: 11, color: C.burgundy }}>• {f.regulation}: {f.finding}</div>
            ))}
          </div>
        )}
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Denial Rate by Demographic Group</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {["Group","Applications","Originated","Denied","Denial Rate","Peer Median","Disparity"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FAIR_LENDING_DATA.denialRates.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}`, background: r.disparityFlag ? "#FEF3C7" : i%2===0 ? "transparent" : C.surface }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600, color: C.text }}>{r.group}</td>
                  <td style={{ padding: "8px 10px", color: C.muted }}>{r.apps}</td>
                  <td style={{ padding: "8px 10px", color: C.forest }}>{r.originated}</td>
                  <td style={{ padding: "8px 10px", color: C.burgundy }}>{r.denied}</td>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: r.denialRate > 0.20 ? C.burgundy : r.denialRate > 0.12 ? C.gold : C.text }}>{(r.denialRate*100).toFixed(1)}%</td>
                  <td style={{ padding: "8px 10px", color: C.muted }}>{(r.peerMedian*100).toFixed(1)}%</td>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: r.disparity > 0 ? C.burgundy : C.forest }}>{r.disparity > 0 ? "+" : ""}{(r.disparity*100).toFixed(1)}pp {r.disparityFlag ? "⚠" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Rate Pricing Disparity</div>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {["Group","Avg Rate","Peer Avg","Spread","Status"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: .4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FAIR_LENDING_DATA.pricingDisparity.map((r, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}`, background: r.flag ? "#FEF3C7" : i%2===0 ? "transparent" : C.surface }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600, color: C.text }}>{r.group}</td>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: C.text }}>{r.avgRate.toFixed(2)}%</td>
                  <td style={{ padding: "8px 10px", color: C.muted }}>{r.peerAvg.toFixed(2)}%</td>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: r.spread > 0.10 ? C.burgundy : r.spread > 0 ? C.gold : C.forest }}>{r.spread > 0 ? "+" : ""}{r.spread.toFixed(2)}%</td>
                  <td style={{ padding: "8px 10px" }}>{r.flag ? <Bdg label="MONITOR" c={C.gold} /> : <Bdg label="OK" c={C.forest} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Active Exam Findings</div>
        {findings.map((f, i) => (
          <div key={i} style={{ display: "flex", gap: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
            <Bdg label={f.severity.toUpperCase()} c={sevColor(f.severity)} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted }}>{f.regulation}</div>
              <div style={{ fontSize: 12, color: C.text }}>{f.finding}</div>
            </div>
          </div>
        ))}
        <AiBox text={`Black/African American applicants are denied at 26.3% vs. 18.4% peer median — a 7.9pp gap that exceeds CFPB's 5pp monitoring threshold and will be a focus area in any fair lending examination. The pricing disparity (Hispanic/Latino +13bps, Black +14bps above peer) is not explained by FICO or LTV alone — a regression analysis is recommended before the next FDIC exam to document legitimate credit factors. LMI lending at 18.4% is 4.6pp below the CRA goal of 23%. Recommend targeted LMI outreach in tracts 2011.01 and 2019.07 to close the gap before Q4 CRA exam.`} c="#2C6B5F" source="FFIEC HMDA Peer Data 2025 · CFPB Fair Lending ECOA Guidance · Internal LAR Analysis" />
      </div>
    </DetailModal>
  );
}

// ── DERIVATIVES BOOK DETAIL ────────────────────────────────────────
export function DerivativesDetail({ onClose }: { onClose: () => void }) {
  const statusColor = (s: string) => s === "Active" ? C.forest : s === "Maturing" ? C.gold : C.muted;
  return (
    <DetailModal title="Interest Rate Derivatives — ASC 815 Hedge Book" onClose={onClose} accentColor="#1E3A5F">
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <StatRow stats={[
          ["Total Notional", "$150M", C.text],
          ["Net Fair Value", `-$${(Math.abs(DERIVATIVES_BOOK.netFairValue)/1e6).toFixed(2)}M`, C.burgundy],
          ["Hedge Effectiveness", `${(DERIVATIVES_BOOK.hedgeEffectiveness*100).toFixed(0)}%`, DERIVATIVES_BOOK.hedgeEffectiveness >= 0.80 ? C.forest : C.burgundy],
          ["Collateral Posted", `$${(DERIVATIVES_BOOK.collateral.posted/1e6).toFixed(1)}M`, C.muted],
        ]} />
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Active Swap Portfolio</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {DERIVATIVES_BOOK.swaps.map((sw, i) => (
            <Card key={i} style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{sw.id} — {sw.type}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{sw.counterparty} · Maturity {sw.maturity} · {sw.hedgeType} Hedge</div>
                </div>
                <Bdg label={sw.status} c={statusColor(sw.status)} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                {[
                  ["Notional", `$${(sw.notional/1e6).toFixed(0)}M`],
                  ["Pay Rate", `${typeof sw.payRate === "number" ? sw.payRate.toFixed(2)+"%" : sw.payRate}`],
                  ["Receive", `${sw.receiveIndex} (${sw.currentReceive.toFixed(2)}%)`],
                  ["Fair Value", `${sw.fairValue < 0 ? "-" : "+"}$${(Math.abs(sw.fairValue)/1e3).toFixed(0)}K`],
                  ["Effectiveness", `${(sw.effectiveness*100).toFixed(0)}%`],
                ].map(([l,v],j) => (
                  <div key={j} style={{ background: C.surface, borderRadius: 6, padding: "8px 10px" }}>
                    <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase", letterSpacing: .5, marginBottom: 3 }}>{l}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{v}</div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Risk Metrics</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>Value at Risk (99% CI)</div>
            <KVList rows={[
              ["1-Day VaR", `$${(DERIVATIVES_BOOK.vaR.oneDay/1e3).toFixed(0)}K`, C.gold],
              ["10-Day VaR", `$${(DERIVATIVES_BOOK.vaR.tenDay/1e3).toFixed(0)}K`, C.gold],
              ["Confidence", `${(DERIVATIVES_BOOK.vaR.confidence*100).toFixed(0)}%`, C.text],
            ]} />
          </Card>
          <Card style={{ padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>Collateral & CSA</div>
            <KVList rows={[
              ["Posted", `$${(DERIVATIVES_BOOK.collateral.posted/1e6).toFixed(1)}M`, C.text],
              ["Threshold", `$${(DERIVATIVES_BOOK.collateral.threshold/1e6).toFixed(0)}M`, C.muted],
              ["Available Capacity", `$${(DERIVATIVES_BOOK.collateral.availableCapacity/1e6).toFixed(1)}M`, C.forest],
            ]} />
          </Card>
        </div>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>Model Governance (ASC 815)</div>
        <Card style={{ padding: 14 }}>
          <KVList rows={[
            ["Last Validation", DERIVATIVES_BOOK.asc815.lastValidation, C.text],
            ["Validator", DERIVATIVES_BOOK.asc815.validator, C.text],
            ["Next Validation", DERIVATIVES_BOOK.asc815.nextValidation, C.muted],
            ["Model Tier", DERIVATIVES_BOOK.asc815.modelTier, C.gold],
            ["Limitations", DERIVATIVES_BOOK.asc815.limitations, C.muted],
          ]} />
        </Card>
        <AiBox text="SW-003 matures March 31 — the $25M Pay Float/Receive Fixed swap will expire in 29 days. As this swap was protecting fixed-rate CRE exposure, CRO should evaluate replacement with a new swap or accept the resulting rate exposure. Fair value of -$620K will be realized through P&L upon maturity. Overall hedge effectiveness of 94% is within the ASC 815 80–125% test range — portfolio remains properly documented." c="#1E3A5F" source="KPMG Hedge Effectiveness Report · Bloomberg Swap Analytics · FASB ASC 815" />
      </div>
    </DetailModal>
  );
}

// ── MODEL GOVERNANCE DETAIL ────────────────────────────────────────
export function ModelGovernanceDetail({ onClose }: { onClose: () => void }) {
  const statusColor = (s: string) => s === "Validated" ? C.forest : s === "Due" ? C.gold : s === "Overdue" ? C.burgundy : C.muted;
  return (
    <DetailModal title="Model Governance Registry — FDIC / OCC Model Risk" onClose={onClose} accentColor="#2C5F3F">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatRow stats={[
          ["Total Models", `${MODEL_GOVERNANCE.length}`, C.text],
          ["Validated", `${MODEL_GOVERNANCE.filter(m=>m.status==="Validated").length}`, C.forest],
          ["Due for Review", `${MODEL_GOVERNANCE.filter(m=>m.status==="Due").length}`, C.gold],
          ["Overdue", `${MODEL_GOVERNANCE.filter(m=>m.status==="Overdue").length}`, C.burgundy],
        ]} />
        {MODEL_GOVERNANCE.filter(m=>m.status==="Overdue"||m.status==="Due").map((m,i) => (
          <div key={i} style={{ background: m.status==="Overdue" ? "#FEF2F2" : "#FEF3C7", border: `1px solid ${statusColor(m.status)}`, borderRadius: 8, padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: statusColor(m.status) }}>{m.status==="Overdue" ? "🚨" : "⚠"} {m.id} {m.name} — Validation {m.status}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Next validation was: {m.nextVal} · Owner: {m.owner}</div>
          </div>
        ))}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {MODEL_GOVERNANCE.map((m, i) => (
            <Card key={i} style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{m.id} — {m.name}</span>
                    <Bdg label={m.tier} c={m.tier==="Tier 1" ? C.burgundy : C.gold} />
                    <Bdg label={m.status} c={statusColor(m.status)} />
                  </div>
                  <div style={{ fontSize: 11, color: C.muted }}>{m.type} · Owner: {m.owner} · Vendor: {m.vendor}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: m.matScore >= 80 ? C.forest : m.matScore >= 65 ? C.gold : C.burgundy }}>{m.matScore}</div>
                  <div style={{ fontSize: 9, color: C.dim, textTransform: "uppercase" }}>Maturity</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: C.muted }}>Last validated: <strong style={{color:C.text}}>{m.lastVal}</strong> by {m.validator}</div>
                <div style={{ fontSize: 11, color: C.muted }}>Next due: <strong style={{color:statusColor(m.status)}}>{m.nextVal}</strong></div>
              </div>
              <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>⚠ Limitations: {m.limitation}</div>
              <div style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>Reg ref: {m.ref}</div>
            </Card>
          ))}
        </div>
        <AiBox text="MOD-004 (Credit Migration) is due for validation this month — if not completed by March 31, it becomes overdue during a period when it is actively being used for watchlist decisions. MOD-006 (Deposit Profitability) is already overdue and uses a transfer pricing methodology from 2022 that predates the current rate cycle. All Tier 1 models require annual external validation per OCC 2011-12. Recommend scheduling external validators for both overdue models in Q2 2026." c="#2C5F3F" source="OCC Bulletin 2011-12 Model Risk · FDIC FIL-22-2022 · Internal Model Inventory" />
      </div>
    </DetailModal>
  );
}
